import {STRATEGIC_PARTNER} from "../constants/strategicPartners";
import {currentYear, languages} from "./common";

const {
    LABEL,
    LEGAL: {
        INSURANCE_NAME,
        SERVICE_NAME
    },
    SHORT_LABEL
} = STRATEGIC_PARTNER;

export const localization = {
    [languages.ENGLISH]: {
        expiredToken: {
            title: "Link has expired",
            descr: `Enter your email address and we’ll send you a new link to access ${LABEL}—`,
            email: "Email address",
            sendLink: "Send link",
            error: "Unexpected error",
        },
        requestToken: {
            title: "Your request was sent",
            descr: `Check your email for an updated link. If we have an account associated with that email, you’ll receive an email with a new link to access ${LABEL}.`,
            didntReceive: "Didn't receive an email?",
            either: "Either ",
            reSend: "re-send the link",
            to: " or ",
            tryDiff: "try a different email",
            stillNeed: `Still need help? Reach out to our team ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} or `,
        },
        appHeader: {
            join: `Join ${LABEL}`,
            getHelp: "Get help",
            tips: "Tips",
            callUsAt: "Call us at",
            monFri: "Mon - Fri",
            sat: "Sat",
            forEe: "For Employees",
            forEr: "For Employers",
            forBr: "For Brokers",
            aboutUs: "About Us",
        },
        benAdminWelcome: {
            title:  `Hi, welcome to ${LABEL}!`,
            employerPasscode: "Enter employer passcode",
            associationPasscode: "Enter association passcode",
            subTitle: "$1 is offering a new insurance plan to complement your health insurance.",
            description: `Even with great health insurance, health issues can be expensive. ${LABEL} provides fast financial support when you're sick or injured.`,
            nextButton: "Next step",
            invalidInformation: "Invalid information",
            stillHavingTr: "Still having trouble?",
            checkWithYour: `Check with your employer, or contact us at ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            checkWithYourAssociation: `Check with your association, or contact us at ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            or: "or ",
            thatPasscode: "That passcode didn't work, please try again.",
        },
        individualWelcome: {
            title: `Welcome to ${LABEL}!`,
            subTitle: `${LABEL} supplemental insurance helps cover\nout-of-pocket medical costs.`,
            description: `${LABEL} offers multiple plans to fit your needs. Let’s review your coverage needs\nand see what it will cost. When you’re ready, confirm your elections and enroll in minutes.`,
            passcode: "Enter Passcode",
            nextButton: "Next step",
            stillHavingTr: "Still having trouble?",
            checkWithYour: `Contact us at ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            or: "or ",
            thatPasscode: "That passcode didn't work, please try again.",
        },
        welcome: {
            title: `Hi $1,<br />welcome to ${LABEL}!`,
            subTitle: `$1 is offering you ${LABEL}’s supplemental health insurance to help with out-of-pocket medical costs.`,
            description: `${LABEL} offers multiple plans to fit your needs. Let’s review your coverage needs and see what it will cost. When you’re ready, confirm your elections and enroll in minutes.`,
            nextButton: "Next step",
            invalidInformation: "Invalid information",
        },
        verifyItsYou: {
            subTitle: "So we know it’s you, please tell us your—",
            birthdate: "Date of birth",
            ssn: "Social Security Number",
            nextButton: "Next step",
            weWereUnable: "We were unable to verify your information. Please try again.",
            stillHavingTr: "Still having trouble? Check with your employer, or contact us at",
            or: "or",
            accNotFound: "Account not found for this converted lead",
            enrollmentReceivedDate: "Enrollment received date",
            classNotFound: "We're having an issue verifying your eligibility details",
            pleaseContact: `Please contact ${LABEL} at`
        },
        benVerifyItsYou: {
            subTitle: "To begin, please tell us your—",
            firstName: "First name",
            birthdate: "Date of birth",
            ageImpact: "Your age impacts cost, and helps us personalize a recommendation for you.",
            nextButton: "Next step",
            weWereUnable: "We were unable to verify your information. Please try again.",
            stillHavingTr: "Still having trouble? Check with your employer, or contact us at",
            whereYouLive: "Where you live impacts what coverage you may be eligible for.",
            coverageNotAvailableForSelectedState: "Coverage not available for selected state",
            or: "or",
            stateOfResidence: "State of Residence"
        },
        steps: {
            step1: "Meet AMI",
            step2: "Personalize",
            step3: "Select",
            step4: "Review",
        },
        learnAboutBrella: {
            title: `Nice to meet you, $1.\nWe're ${LABEL}.`,
            subTitle: "We’re on a mission to ease the financial impact when health hardship strikes.",
            description: `${LABEL} complements your primary health insurance and pays you a cash benefit if you’re diagnosed with any of 13,000+ covered conditions. Use your money towards anything else you need on your road to recovery.`,
        },
        tellUsAboutYou: {
            title: "Tell us a little more about you",
            description: "Knowing about your family will help us recommend a plan for you—",
            maritalStatus: "Are you married?",
            im: "I'm",
            selectOne: "select one",
            dependents: "Do you have children?",
            ihave: "I have",
            no: "No",
            yes: "Yes",
            noKids: "I'm not a parent",
            aKid: "Yes, my youngest child is under 18 years old",
            aKidGrown: "Yes, my youngest child is over 18 years old",
            includesDpAndCu: "Includes domestic partnerships and civil unions",
            includesOnlyDp: "Includes domestic partnerships",
            includesOnlyCu: "Includes civil unions",
        },
        thanksWantToHearMore: {
            title: "Thanks, $1",
            subTitle: `We’d love to tell you a little more about how ${LABEL} helps.\nThen we’ll work on recommending a plan for you.`,
        },
        whyBrella: {
            title: "We know costs like these can be scary",
            p1: "Even with great health insurance, most people have deductibles and copays that can lead to out-of-pocket costs.",
            p2: `${LABEL} helps by paying you a lump sum if you’re diagnosed with a covered condition. That way health issues are less likely to drain your savings or end up on your credit card.`,
            source: "Source: Healthcare Bluebook, Feb 2021. Your costs may be more or less depending on your health insurance coverage.",
            cut: "A small cut",
            dehydration: "Dehydration",
            torn: "Torn ACL",
            appendicitis: "Appendicitis",
            heart: "Heart attack"
        },
        frustrations: {
            title: `${LABEL} can help ease the financial burden of unexpected medical expenses`,
            subTitle: "Our supplemental health insurance pays a cash benefit if you’re diagnosed with a covered condition to help ease the burden of unexpected medical costs.",
            t1: "We cover 13,000+ conditions",
            t2: "Claims are simple and easy",
            t3: "Our team is standing by",
            d1: `${LABEL} pays cash benefits based on your covered diagnosis.`,
            d2: "File a claim in minutes with info you may have on hand. Once approved get paid in hours—not weeks.",
            d3: "Talk to the same person any time you have a question. We’re always here to help.",
        },
        diagnoses: {
            title: `How ${LABEL} works`,
            subTitle: "We cover a wide spectrum of conditions from concussions to cancer. Each condition is assigned to a category, which determines the condition’s benefit amount.",
            payouts: "Conditions in all three benefit categories are covered—",
            upTo: "Up to",
            forOverXConditions: "for $1+ conditions",
            examples: "Examples",
            or: "Or",
            coverYourself: "to cover just yourself",

            descrM: "Conditions that require a short visit to the ER or urgent care for diagnosis",
            exampleM: "Broken arm, pneumonia, concussions, small cuts and many more...",

            descrS: "Serious conditions that often require surgery or more intensive treatment",
            exampleS: "Appendicitis, benign cancers, torn meniscus, and lots more...",

            descrC: "Dangerous or life-threatening conditions that require immediate medical intervention",
            exampleC: "Heart attack, stroke, malignant cancers, and some other tough stuff...",

            descrNC: "Conditions that can't\nbe treated through\nhome care or a visit\nto your PCP",
            exampleNC: "Flu\nsome other stuff you\ndon't care about",

            planStartsAt: "Premiums start at $",
            perPaycheck: "per paycheck ",
            perPayPeriod: "per pay period ",
            perMonth: "per month ",

            forFamilyCoverage: "for family coverage",
            forSpouseCoverage: "for coverage for you and your spouse",
            forChildrenCoverage: "for you and your child(ren)",

            footerDescr: "In case you're curious about the few conditions we don't cover, it's common conditions\nthat happen to us all on a routine basis (e.g. headaches, common cold, etc)- they can be\nthough on us but they're unlikely to break the bank.",
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",

            choose: "You’ll choose your benefit amounts in a few steps.",

            imgName: "SPECTRUM",
            moderateConditions: "6,000+ conditions",
            severeConditions: "5,600+ conditions",
            catastrophicConditions: "1,500+ conditions"
        },
        conditionDetails: {
            title: "What’s covered?",
            subTitle: "Explore below to learn about what’s covered. Don’t worry—we don’t exclude pre-existing conditions. Coverage is guaranteed issue, no medical questions asked.",
            fullListLink: "View all conditions",
            fullListDisclaimer: "Includes Limitations and Exclusions",
            covers: `${LABEL} covers conditions in all three categories below—`,

            moderateTitle: "Moderate conditions",
            moderateWhatIsTitle: "What’s a moderate condition?",
            moderateWhatIsText: "Injuries or illnesses that likely require a short visit to the ER or urgent care.",
            moderateCoversTitle: "Covers 6,000+ conditions,",
            moderateCoversText: "including simple fractures, lacerations, dehydration, and kidney stones.",
            moderatePayoutContent: "Receive a Moderate benefit amount once per insured per covered diagnosis every $1 days. There is no limit to the number of Moderate benefits you can receive.",

            severeTitle: "Severe conditions",
            severeWhatIsTitle: "What’s a severe condition?",
            severeWhatIsText: "Serious conditions that require more intensive medical treatment and attention.",
            severeCoversTitle: "Covers 5,600+ conditions,",
            severeCoversText: "including appendicitis, compound fractures, pulmonary embolism, and torn ACL.",
            severePayoutContent: "Receive a Severe benefit amount once per insured per covered diagnosis every $1 days. There is no limit to the number of Severe benefits you can receive.",

            catastrophicTitle: "Catastrophic conditions",
            catastrophicWhatIsTitle: "What’s a catastrophic condition?",
            catastrophicWhatIsText: "Dangerous or life-threatening conditions that require immediate medical intervention.",
            catastrophicCoversTitle: "Covers 1,500+ conditions,",
            catastrophicCoversText: "including malignant lung cancer, heart attack, stroke, and major organ failure.",
            catastrophicPayoutContent: "Receive a Catastrophic benefit amount once per insured per covered diagnosis every $1 days. An insured may claim a Catastrophic benefit a maximum of $2x for diagnoses in the same condition grouping.",

            learnMore: "Learn more.",
            hide: "Hide",
            see: "See",
            categoryDetails: "category details",

            choiceIncrements: "Choose a benefit ammount from ",
            selectPayout: "Choose a benefit ammount of ",

            whatsNotCovered: "What's not covered?",
            uncovered: `${LABEL} doesn’t cover everything. It is designed to cover specific moderate, severe, and catastrophic conditions. View all covered conditions and review our Limitations and Exclusions to learn more.`,
            or: "or"
        },
        yourBrellaPlanAlsoIncludes: {
            title: "Additional Benefits",
            yourBrellaPlan: "Your coverage also includes these conditions—",
            payoutListed: "the benefit ammount for these conditions is listed.",
            chronic: "Chronic Conditions",
            chronicDescription: "chronic",
            mental: "Mental Illness",
            maintenance: "Maintenance",
            maintenanceDescription: "maintenance",
            maternity: "Maternity",
            maternityDescription: "maternity",
            chronicDetails1: "Receive coverage for over ",
            chronicDetails2: " additional $riderLabel conditions spanning asthma, diabetes, epilepsy, and many more.",
            chronicDetailsAmount: "4,100",
            mentalDetails1: "Additional coverage for ",
            mentalDetails2: " different $riderLabel health disorders like anxiety, bipolar, and depression.",
            mentalDetailsAmount: "600",
            maternityDetails1: "Expands coverage to include an additional benefit category for nearly ",
            maternityDetailsAmount: "2,300",
            maternityDetails2: " covered $riderLabel conditions.",
            maternityDetails3: " A benefit may be payable if an Insured becomes pregnant or is diagnosed with a $riderLabel Condition while covered by the Policy.",
            maternityDetails4: " Policies issued in North Carolina must include $riderLabel benefits/coverage.",
            nextButton: "Next",
            seeFullList: "See the full list of conditions",
        },
        whatsCovered: {
            title: "Conditions list",
            subTitle: "Browse from $1 conditions",
            mostCommon: "Most common",
            searchingFor: "Searching for ",
            allOther: "All other",
            bodyPart: "Body part",
            allConditions: "All conditions",
            mostCommonConditions: "Most common conditions",
            nextButton: "Continue",
            description: "Description",
            code: "Code",
            searchFullCond: "Search full conditions list",
            viewLimit: "View Limitations and Exclusions",
            errLoading: "It was not possible to load condition information",
            search: "Search",
            totalResults: " total results for ",
            brellaPays: `${LABEL} pays benefits when you’re diagnosed with any of 13,000+ covered conditions as provided for in the policy. To learn more about what’s covered and what’s not, try searching by either the ICD-10 code, condition or browse the list for the diagnosis. You’ll see conditions listed with the benefit category they are assigned in your contract. We’ve used laymans’ terms for injuries and illnesses here, but physicians often use different scientific names and assign a variety of ICD-10 diagnosis codes to identify and distinguish between conditions. To avoid confusion, when you file a claim we will evaluate whether or not you have a covered condition based on the ICD-10 code your physician uses to describe your diagnosis. If you have any questions about coverage for specific conditions, contact us at ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} or email `,
            cantFind: "Can’t find what you need?",
            callBrella: `Call ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            or: " or ",
            emailOurTeam: "email our team",
            includedInPlan: "Included",
            notIncludedInPlan: "Not included",
            viewMore: "View More"
        },
        claims: {
            title: "How to use your benefits",
            subTitle: "Claims can be a nightmare",
            description: `Say goodbye to pages of paper forms and long processing times. ${LABEL} makes it easy to file a claim online, so you’re paid in hours—not weeks.`,
            receiveMedicalAttention: "Get medical\nattention",
            tellUsWhat: "Tell us what\nhappened",
            uploadPhotos: "Upload photos or\ndocuments",
            analyzeClaim: "We'll analyze your\nclaim",
            setPaymentPreference: "Set your payment\npreference",
            ifYourClaimIsApproved: "If your claim is approved, we’ll pay it in less than 72 hours.",
            otherwiseOurFriendly: "Otherwise our team will reach out to you about next steps.",
            seeFullList: "See the full list of conditions",
        },
        enrollmentProblem: {
            enrollmentIssue: "Enrollment issue",
            unableToRetrieve: "We are unable to retrieve your plan information for\nenrollment. Please reach out to our team\nif you have any questions."
        },
        notEligible: {
            eligibilityIssue: "Eligibility issue",
            basedOnThe: `Based on the information you've provided, you aren't eligible for coverage at this time.\nIf you feel like this is incorrect, please reach out to your employer.`,
            basedOnTheAssociation: `Based on the information you've provided, you aren't eligible for coverage at this time.\nIf you feel like this is incorrect, please reach out to your association.`,
        },
        howCanBrellaHelp: {
            title: `See how ${LABEL} helped`,
            description: `Here's how ${LABEL} works for families and health issues of all shapes and sizes—\nnote, these aren't real scenarios but they're realistic illustrations to help you better understand how ${LABEL} helps.`,
            nextButton: "Find my plan",
            ray: "Ray, age 56",
            rayDescr: `Ray is divorced father of two adult kids. He has employer-sponsored health insurance plus ${LABEL}.`,
            amber: "Amber, age 31",
            amberDescr: `Amber is a single mother of an infant daughter. She covers herself and her daughter with health insurance plus ${LABEL}.`,
            serena: "Serena, age 49",
            serenaDescr: `Serena is married mother of three. She has employer-sponsored health insurance plus ${LABEL}.`,
            marcus: "Marcus, age 38",
            marcusDescr: `Marcus is married with 4 children, and his whole family is covered by health insurance plus ${LABEL}.`,
        },
        howYourPlanPays: {
            title: `How ${LABEL} pays`,
            description: `${LABEL} is separate from your health insurance. If you’re diagnosed with a covered condition, let us know by filing a claim. If approved, we’ll pay you the benefit amount for the category to which your condition belongs.`,
            payoutsTitle: "We’ll help you set your benefit amounts in a few steps.",
            payoutComment: "Your benefit amount is the same for all covered conditions in the category regardless of what your health insurance plan does or doesn’t cover.",
            seeHow: `See how ${LABEL} paid`,
            readMore: "Read more.",
            howBenefited: `Here's how a few different people benefited from ${LABEL}.`,
            scenarios: `Note, these aren’t real scenarios but they’re realistic illustrations to help you better understand how ${LABEL} helps.`,
            readStories: "Read user stories",
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            conditions: "conditions",
            choiceIncrements: "Choose a benefit amount ranging from ",
            selectPayout: "Choose a benefit amount of ",
        },
        healthPlanDetails: {
            title: "Let’s talk about medical expenses",
            subTitle: "Tell us about your health insurance—",
            budget: "We’ll keep your budget in mind",
            subTitleFinancial: "Tell us a bit about your financial situation—",
            doesYourHealth: "Do you feel that your deductible is affordable?",
            yes: "Yes",
            no: "No",
            imNot: "I'm not covered by health insurance",
            whenVisitingDoctor: "When visiting the doctor or picking up a prescription...",
            iPay: "I typically pay copays",
            copays: "copays",
            theFullCost: "I typically pay the full cost until I reach my deductible",
            iDontWant: "or I don't want a recommendation.",
            skip: "Skip this",
            income: {
                disposableIncome: "Dispossable Income",
                afterYouPay: "After you pay for your ",
                necesities: "necessities",
                houseingCar: " (housing, car, loans, utilities, & food), about how\nmuch money do you have leftover",
                eachMonth: "each month",
                forDiscretionary: " for discretionary spending?",
                disclaimer: "*Don't worry, we won't share this information with your employer.",
                disclaimerAssociation: "*Don't worry, we won't share this information with your association.",
                selectOne: "select one",
                opt1: "I spend it all on necessities",
                opt2: "I'm able to buy 1-2 \"nice to haves\" each month",
                opt3: "I generally can purchase things I want without thinking too much",
                opt4: "I can purchase what I want and add to my savings",
            },
        },
        plan: {
            title: "Our recommendation for you",
            basedOn: "Based on the information you provided, we recommend the following. Use the sliders to select or adjust your payouts and who’s covered.",
            basedOnAlternate: "Based on the information you provided, we recommend",
            basedOnAlternate2: "You can choose or adjust your selection and who's covered below—",
            basedOnModal: "See why we made this recommendation",
            enhanced: "Enhanced",
            value: "Value",
            premier: "Premier",
            getTips: "and get tips for customizing each category→",
            descriptionGreen: "See why we chose these amounts",
            titleSkipped: `Select your ${LABEL} benefits`,
            descriptionSkipped: "Need some help? Answer 3 questions to get a",
            descriptionGreenSkipped: "personalized recommendation",
            conditions: "Conditions",
            recommended: "Recommended",
            iWantToCover: "I want to cover",
            costPer: "Cost per",
            employerContrib: "Employer contribution",
            employeeContrib: "Your cost",
            coverMySelf: "Myself",
            coverSpouse: "My spouse and me",
            coverChild: "My child(ren) and me",
            coverFamily: "My family and me",
            coverSpouseUnderscore: "My __spouse__ and me",
            coverChildUnderscore: "My __child(ren)__ and me",
            costPerOpt1: "Paycheck",
            justAMoment: "Just a moment...",
            jamDescr: "We're personalizing your plan",
            okay: "Okay, got it",
            categoryExp: "Category details",
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            emailMyRecommendation: "Email my recommendation",
            emailMyRecommendationDescr: "Please enter your email address below to receive a copy of your recommendations—",
            emailAddress: "Email address",
            cancel: "Cancel",
            submit: "Submit",
            emailSubmitted: "Your email has been sent!",
            submitEmailErrorMessage: "An error has occurred, your email has not been sent! Please try again later.",
            goToYourEnrollmentPlat: `Visit your enrollment platform to submit your ${LABEL} selections. If you'd like, you can take these recommendations to go and reference them when you sign up.`,
            readyToMakeYourSelec: "Ready to enroll?",
            printRecomm: "Print recommendations",
            emailRecomm: "Email recommendations",
            dontSeeCoverageTier: "Don't see a coverage tier? Make sure to",
            dontSeeCoverageTierSelect: "Don't see a dependent coverage tier? Go back and",
            editYourFamily: "edit your family details.",
            goBackAndEdit: "go back and edit",
            yourFamilyDetails: "your family details.",
            descrM: "Examples include simple fractures, lacerations, dehydration, and kidney stones.",
            descrS: "Examples include appendicitis, compound fractures, pulmonary embolism, and torn ACL.",
            descrC: "Examples include malignant lung cancer, heart attack, stroke, and major organ failure.",
            seeWhyWeChoseTitle: "Recommendation overview",
            modalP1: `Your needs are unique to you. That’s why we offer multiple ${LABEL} (AMI) plans—so you can choose the benefit amounts that are right for you. We assess a number of different factors to make a recommendation. Here’s how each factor comes into play.`,
            modalP2Title: "Age",
            modalP2: `The types and severity of health issues we face changes over time. As we get older, the risk of serious and dangerous medical issues increases, so we generally recommend a plan with higher benefit amounts for the Severe and Catastrophic benefit categories.`,
            modalP3Title: "Family status",
            modalP3: "Your family makeup helps us understand who is eligible for coverage and make some assumptions about their health risks. Given their zest for life, children generally tend to have less serious but more frequent injuries and illnesses, so if you have children we recommend plans with higher Moderate benefit amounts.",
            modalP4Title: "Medical expenses",
            modalP4: "How you feel about the affordability of your primary health plan’s deductible and your share of costs give us a sense of your financial exposure in the event of an unexpected health issue. If you have concerns about your deductible and / or health cost responsibilities we recommend higher benefit amounts across all three categories.",
            modalP5Title: "Your budget",
            modalP5: "Your discretionary income helps us recommend a plan with premiums that work for your budget. If you don’t have much left over at the end of the month, we might recommend an AMI plan with a more affordable premium. For those with more disposable income, we may recommend more coverage in line with other factors like your age and family status.",
            seeWhyWeRecommendedTitle: "Why we recommended this plan",
            recommendedForYou: "-Recommended-",
            iDontWant: `I don't want to partcipate in the ${LABEL} insurance plan`,
            or: "or",
            yourOpenEnrollment: "Your open enrollment begins on $1. Come back then to finalize your elections. Don't worry, we'll save your progress for when you come back.",
            paycheck: 'paycheck',
            perPaycheck: 'Per paycheck',
            monthly: 'Monthly',
            month: 'month',
            employerContribution: "Employer contribution",
            payouts: "Payouts",
            important: "IMPORTANT",
            basedOnBenefit: "Based on your age, you will receive $1% of any benefit you select here.",
            nextSteps: "Next steps",
            includesDpAndCu: "Includes domestic partnerships and civil unions.",
            includesOnlyDp: "Includes domestic partnerships.",
            includesOnlyCu: "Includes civil unions.",
            childrenAreElegible: "Child age restrictions apply.",
            paycheckInfo: "Estimated based on a payroll frequency of $1 payroll deductions per year, and may not match exact payroll withdrawals. Contractual premiums are on a monthly basis.",
            sliderWarningLabel: "Please review.",
            sliderWarningDescription: "Your benefit payout for {{plan1}} must be less than {{plan2}}.",
            thisProductIsOnlyAvailable: "This product is only available to individuals who have in-force comprehensive health insurance.",
        },
        summary: {
            title: "Review your selection",
            description: "Here are the coverage details you’ve selected—",
            selectOffering: "Select offering",
            planName: "Plan name",
            benefitAmounts: "Benefit amounts",
            additionalCoverage: "Additional coverage",
            employerContrib: "Employer contribution",
            yourCost: "Your cost",
            effectiveDate: "Effective date",
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            whosCovered: "Who's covered",
            chronic: "Chronic Conditions",
            mental: "Mental Illness",
            maintenance: "Maintenance",
            or: "or",
            goBack: "Go back and edit",
            important: "IMPORTANT",
            basedOn: "Based on your age, you will receive $1% of any benefit you select here.",
            note: "Note",
            youveAlready: `You've already made your ${LABEL} elections, If you need to make a change, `,
            reachOut: "reach out to our team →",
            youllReceive: "You'll receive a notification to finish setting up your account!",
            mo: "mo",
            paycheck: "paycheck",
            coverMySelf: "Myself",
            coverSpouse: "My spouse and me",
            coverChild: "My child(ren) and me",
            coverFamily: "My family and me",
        },
        thanksDeclined: {
            thankYou: "Thank you, $1",
            youHaveSuccessfully: `You have successfully declined ${LABEL} coverage.`,
            toMakeAChange: "To make a change, ",
            contactOurConcierge: "contact our team →",
        },
        additionalInfo: {
            yourTier: "Covering",
            family: "My family and me",
            title: "Final details before we're done",
            homeAddress: "Your address",
            streetAddress: "Street address",
            zipCode: "Zip code",
            state: "State",
            aptSuiteOther: "Apartment",
            city: "City",
            dependents: "Your family",
            spouse: "Spouse",
            addChildT: "Add Child",
            addSpouse: "Add Spouse",
            addDependent: "Add dependent",
            editDependent: "Edit dependent",
            yourDetails: "Your details",
            firstName: "First name",
            middleName: "Middle name",
            middleInitial: "Middle initial",
            lastName: "Last name",
            suffix: "Suffix",
            dob: "Date of birth",
            ssn: "Social Security Number",
            daytimePhone: "Daytime phone number",
            phoneType: "Type of phone",
            role: "Role",
            relationship: "Relationship to employee",
            domesticPartner: "Domestic partner",
            civilUnion: "Civil union",
            address: "Address",
            gender: "Gender",
            sameAsEmployee: "Same as employee",
            details: "Details",
            cancel: "Cancel",
            submit: "Submit",
            save: "Save",
            optional: "optional",
            theGreyedOut: "The greyed out fields were provided by your employer, please reach out to them if they need to be corrected or updated.",
            invalidPhoneNumber: "Invalid Phone Number",
            invalidZipCode: "Invalid Zip code",
            note: "Note: ",
            reqSpouseChild: "The coverage tier you selected requires you add your spouse and at least one child.",
            reqSpouse: "The coverage tier you selected requires you add your spouse.",
            pleaseAddAnother: "Please add another dependent or ",
            closeAnd: "close and go back ",
            changeCover: "and change coverage tier.",
            reqChild: "The coverage tier you selected requires you add at least one child.",
            delete: "Delete",
            confirmDelete: "Confirm delete",
            dependent: "Dependent ",
            willBeRemoved: " will be removed.",
            theCoverageTierDep: " The coverage tier you selected doesn’t include dependents.",
            editDetails: "Edit details ",
            coverageNoChild: "The coverage tier you selected doesn't cover children. Please ",
            coverageNoSpouse: "The coverage tier you selected doesn't cover a spouse. Please ",
            goBack: "go back",
            andChangeCover: " and change coverage tier or update role to be spouse.",
            andChangeCoverCh: " and change coverage tier or update role to be child.",
            inOrderToDetermin: `In order to determine if your child qualifies for ${LABEL} coverage, please review and acknowledge the following—`,
            inOrderForChildToQualify: `In order for your child to qualify for ${LABEL} coverage, the following must apply—`,
            cIsUnmarried: "the child is unmarried and does not have a dependent of his or her own; and",
            cIsFLResident: "the child is a resident of Florida or is a part-time or a full-time student (must be indicated in the fields above).",
            cIsIncapable: "the child is incapable of self-sustaining employment because of a mental or physical disability; and",
            cIsChiefly: "the child is chiefly dependent on You for support and maintenance; and",
            cProofIs: "proof is provided of the disability upon our request (proof will be required at the time of claim).",
            iAgree: 'I acknowledge that my child meets the criteria above',
            iAckTheCriteriaAppliesToMyChild: 'I acknowledge that the criteria above applies to my child',
            female: "Female",
            male: "Male",
            mobile: "Mobile",
            home: "Home",
            standard: "Standard",
            child: "Child",
            genericErrTitle: "Oops!",
            genericErr: "Please correct the errors above before proceeding.",
            dependentErr: "Your coverage tier requires a dependent. Please add one to move forward.",
            basedOnDependentAge: "Based on your spouse’s age, they will receive $1% of any benefit you select here.",
            important: "IMPORTANT",
            studentStatus: "Student status",
            studentStatusFullTime: "Full-time",
            studentStatusPartTime: "Part-time",
            studentStatusNotAStudent: "Not a student",
            pleaseNoteAboveCriteria: "Please note, at least one of the above criteria must apply to click save.",
            duplicateDependent: "A dependent with this information already exists.",
            requestError: "There was an error in the request",
            firstNameUpdateError: "The First Name can not be changed.",
            lastNameUpdateError: "The Last Name can not be changed.",
            birthdateUpdateError: "The Birthdate can not be changed.",
            ssnUpdateError: "The SSN can not be changed.",
            roleUpdateError: "The Role can not be changed.",
        },
        review: {
            yes: "Yes",
            no: "No",
            title: "Review details",
            nextButton: "Submit",
            personalDetails: "Personal details",
            selectOffering: "Select offering",
            planName: "Plan name",
            benefitAmounts: "Benefit amounts",
            additionalCoverage: "Additional coverage",
            dependents: "Dependents",
            yourCost: "Your cost",
            effectiveDate: "Effective date",
            firstName: "First name",
            lastName: "Last name",
            email: "Email",
            birthdate: "Date of birth",
            address: "Address",
            city: "City",
            state: "State",
            zipCode: "Zip code",
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            chronic: "Chronic Conditions",
            mental: "Mental Illness",
            maintenance: "Maintenance",
            spouse: "Spouse",
            domesticPartner: "Domestic partner",
            civilUnion: "Civil union",
            daughter: "Daughter",
            son: "Son",
            requestError: "There was an error in the request",
            enterPassword: "Enter password",
            enterPasswordAgain: "Enter password again",
            createAccount: "Create account",
            createAccountDescr: "Enter a password to finish creating your account. Your username will be your work email.",
            looksLikeWeAlready: `Look’s like we already have a ${LABEL} account with this email. Enter your ${LABEL} password below to submit your elections—`,
            thisMayBe: `This may be a password you’ve used for a ${LABEL} employer, member, or broker partner portal in the past.`,
            gender: "Gender",
            middleName: "Middle name",
            child: "Child",
            employerContribution: "Employer contribution",
            ssn: "Social Security Number",
            edit: "Edit",
            mo: "mo",
            paycheck: "paycheck",
            beforeFinal: "Before final enrollment, please verify all information is correct—",
            passwordsMust: "Passwords must have—",
            p1: "10 characters.",
            p2: "At least one uppercase letter.",
            p3: "At least one number.",
            pErr1: "Password must have at least 10 characters",
            pErr2: "Password must have at least 1 number",
            pErr3: "Password must have at least 1 upper case letter",
            pErr4: "Passwords do not match.",
            pErr5: "Must agree to terms",
            fraudWarning: "FRAUD WARNING",
            disclaimer1: "FRAUD WARNING: Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison.",
            disclaimer2: "This Policy does not constitute comprehensive health insurance coverage and does not satisfy the requirement of Minimum Essential Coverage under the Affordable Care Act.\n\nNotice: This Policy provides limited benefits when you are diagnosed with certain medical conditions. Benefit amounts are based on whether the diagnoses are classified as moderate, severe, or catastrophic. They are not based on the cost of treatment and are not designed to cover your medical expenses. Not every medical condition is covered under the plan.",
            disclaimer3: "I hereby elect to enroll myself $1as listed above for the benefits available under my Group’s Limited Benefit insurance plan.\nBy signing below (via clicking Submit), I agree $2 receive all documents and correspondence electronically and that I can access the internet and/or have an email address where I can receive email correspondence. I understand that I may revoke this authorization or request specific paper documents without revoking this authorization by contacting the Company (National Union Fire Insurance Company of Pittsburgh, Pa.) or Administrator (Ansel Services, Inc.) by mail, email or telephone.\n\nI represent to the best of my knowledge and belief all information provided by me on this form is true and correct.",
            disclaimerExtraMD: "This is a supplement to health insurance and is not a substitute for major medical coverage. Lack of major medical coverage (or other minimum essential coverage) may result in an additional payment with your taxes.",
            disclaimer3Opt1: "and those members of my family",
            disclaimer3Opt2: "to",
            disclaimer3Opt3: "and understand that I can",
            note: "Note:",
            yourBrellaCoverage: `Your ${LABEL} coverage will be effective when payment is successfully received.`,
            paymentsDetails: "Payment details",
            pleaseAddYour: "Please add your payment details below—",
            byEnteringPayments: `By entering payment details you authorize ${SERVICE_NAME} to charge the bank or financial institution you're designating the total amount listed below.`,
            yourInformationWill: "Your information will be saved to file for future transactions on your account. You may cancel this authorization at any time by contacting us. This authorization will remain in effect until cancelled.",
            youHaveNoEPayment: "You have no e-payment option set up for the initial payment!",
            addBankAccount: "Add bank account",
            addCreditCard: "Add credit card",
            paymentIssue: "Payment issue.",
            thereWasAnIssue: "There was an issue with the payment_method entered below. Please double check the information provided and try again.",
            or: "or",
            iDontWantToMake: "I don't want to make an e-payment. I will mail a check instead.",
            seeInstructions: "See instructions.",
            mailInYourPayment: "Mail in your payment",
            preferToMailInA: "Prefer to mail in a check? Please download the PDF and enclose it with your payment—",
            mailTo: "Mail To",
            downloadPDF: "Download PDF",
            bankAccount: "Bank account",
            creditCard: "Credit Card",
            setItAndForgetIt: "Set it and forget it",
            enableAutoPayToSave: "Enable AutoPay to save your payment method and have your monthly premium charged automatically!",
            weWillNotifyYou: "We’ll still notify you before each payment date, and you can change your AutoPay settings at any time.",
            cancel: "Cancel",
            confirm: "Confirm",
            autoPay: "AutoPay",
            autoPayIsOn: "AutoPay is on",
            enableAutoPay: "Enable AutoPay",
            turnOffAutoPay: "Turn off AutoPay?",
            areYouSureYouWantToTurnOff: "Are you sure you want to turn off automatic payments? You’ll need to make payments manually going forward.",
            savePaymentMethod: "Save payment method for future use",
            iAuthorizeBrellaServices: `I authorize ${SERVICE_NAME} to automatically charge this payment source for all future invoices. You can cancel automatic payments or change settings at any time.`,
            checksShouldBe: `Checks should be payable to ${SERVICE_NAME}`,
            cardNumber: "Card number",
            expiration: "Expiration",
            cvc: "CVC",
            country: "Country",
            useDifferentPaymentMethod: "Use different payment method",
            itLooksLikeYouDidntAdd: "It looks like you didn’t add an electronic payment method. If you continue without adding an electronic payment method, we will expect a paper check payment through the mail. Before continuing, please re-consider using an electronic payment option. If payment is not received in time, that may result in a delay of your coverage.",
            paymentProcessing: "Payment processing...",
            pleaseDoNotRefresh: "Please do not refresh or close your window, ",
            thisProcessMakeTake: "this process may take up to a minute.",
            iAcceptThe: "I accept the",
            terms: "Terms",
            and: "and",
            privacy: "Privacy",
            ccEndingWith: "Ending with",
            goBack: "Go back",
            confirmAndSubmit: "Confirm and submit",
            pendingVerifiction: "Pending verification",
            removePendingverificationModalHeader: "Are you sure you want to use a different payment method?",
            removePendingverificationModalContent: "Switching your payment method will result in the loss of your bank account's verification status. It can take up to an additional 2 business days to re-verify your bank account.",
            removePendingverificationModalConfirm: "I'm sure",
            addBankAccountError: "There was an error trying to add your bank account.",
            genericError: "We are unable to process your enrollment. Please reach out to our team for more information.",
        },
        success: {
            title: "Thanks, $1!",
            description: `Your information has been successfully submitted. Welcome to ${LABEL}!`,
            next: `Next, look out for an email from us confirming your elections.\nWe'll also share instructions for how to set up your ${LABEL} account.`,
            allDone: "All done!",
            feelFree: "Feel free to close your browser tab or",
            logOut: "log out.",
            yourCoverage: "Your coverage starts on"
        },
        categories: {
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            weDontCover: "Not covered",
        },
        brellaHelp: {
            title: `See how ${LABEL} helped $1`,
            viewMoreStories: "View more stories",
            coverage: `$1’s ${LABEL} coverage`,
            moderate: "Moderate",
            severe: "Severe",
            catastrophic: "Catastrophic",
            perPaycheck: "Per paycheck",
            priceIllustrative: "This price is for illustrative purposes only",

            story: "$1’s story",

            title2: `Where ${LABEL} comes in`,
            personPlan: `$1's ${LABEL} plan`,
            personPaid: "$1 paid",
            whoSCovered: "Who's covered",
            costPaycheck: "Cost/paycheck",
            brellaBenefitAmounts: `${LABEL} benefit amounts`,
            nextButton: "Ok, got it",
            unexpectedExpenses: "Unexpected expenses",
            brellaCoverage: `${LABEL} covered ALL of $1’s costs and more`,
            paymentsTitle: "$1's claims",
            paymentsTitleSingluar: "$1's claim",
            brellaPayout: `${LABEL}'s payout`,
            brellaCash: `$1's total ${LABEL} benefit`,
            brellaCashSingular: `$1's total ${LABEL} benefit`,
            subtitle: "The scenario summarized below is offered only as an example. Coverage depends on the actual facts of each case and the terms, conditions and exclusions of the policy.",
            s1: {
                ray: "Ray, age 56",
                description: `Ray is a divorced father of two adult kids. He has employer- sponsored health insurance plus ${LABEL}. His health insurance deductible is $1,500.`,
                injury1Title: "A COVID diagnosis",
                injury1Desc: "After coming down with a fever, Ray tested positive for COVID-19. His symptoms were mild at first but he developed serious complications.",
                injury2Title: "Serious complications",
                injury2Desc: "Ray was diagnosed with pneumonia, acute respiratory failure, and sepsis over several weeks. Fortunately, his condition improved and he was discharged from the hospital.",
                injury3Title: `${LABEL} helps out`,
                injury3Desc: "Ray filed claims and received benefits for pneumonia,  acute respiratory failure, and sepsis. He used the funds to cover medical bills up to his out-of-pocket max and to hire someone to help with groceries and the lawn until he fully recovered.",
                payout1: "Pneumonia has a Moderate benefit amount of",
                payout2: "Acute Respiratory Failure has a Severe benefit amount of",
                payout3: "Sepsis has a Catastrophic benefit amount of"
            },
            s2: {
                amber: "Amber, age 31",
                description: `Amber is a single mother of an infant daughter. She covers herself and her daughter with health insurance plus ${LABEL}. Her health insurance deductible is $1,000.`,
                injury1Title: "Appendicitis surgery",
                injury1Desc: "When Amber woke up with piercing stomach pain, she went to the ER and had surgery for appendicitis.",
                injury2Title: "She filed a claim in minutes...",
                injury2Desc: `She filed a claim with ${LABEL} from her phone the day she went home from the hospital.`,
                injury3Title: "...and got paid in 72 hours.",
                injury3Desc: "Amber's claim was approved in 72 hours. She used the cash to pay a babysitter and have groceries delivered. She also paid the hospital bill when it arrived a few weeks later.",
                payout1: "Appendicitis has a Severe benefit amount of"
            },
            s3: {
                serena: "Serena, age 49",
                description: `Serena is a married mother of three. She has employer- sponsored health insurance plus ${LABEL}. Her health insurance deductible is $5,000.`,
                injury1Title: "A breast cancer diagnosis",
                injury1Desc: "Serena was diagnosed with malignant breast cancer this year. She filed a claim and was approved for a $7,500 Catastrophic benefit.",
                injury2Title: "Ongoing treatment",
                injury2Desc: "Three months later, she was still in active treatment and received another $7,500 Catastrophic benefit.",
                injury3Title: `${LABEL} helps out`,
                injury3Desc: "Serena spent her payouts on medical bills until she reached her out-of-pocket max. The rest helped with transportation and extra help around the house.",
                payout1: "Malignant Breast Cancer gets a Catastrophic benefit amount of",
                payout2: "Malignant Breast Cancer, second Catastrophic benefit amount of "
            },
            s4: {
                marcus: "Marcus, age 38",
                description: `Marcus is married with 4 children, and his whole family  is covered by health insurance plus ${LABEL}. His health insurance deductible is $3,000.`,
                injury1Title: "A broken leg",
                injury1Desc: "Marcus has been having a rough year so far. In January, he fell off a ladder and broke his femur.",
                injury2Title: "The stomach flu",
                injury2Desc: "Weeks later his daughter caught the stomach flu and went to the ER for dehydration.",
                injury3Title: "Recovery",
                injury3Desc: `Marcus filed claims and received ${LABEL} benefits, which he used to pay medical bills toward his deductible. His ${LABEL} benefits eased the financial pressure of these unexpected costs.`,
                payout1: "A broken femur has a Severe benefit amount of",
                payout2: "Dehydration has a Moderate benefit amount of"
            }
        },
        wouldYouMind: {
            iDontWant: "I don't want recommendations,",
            skip: "skip this",
            title: "$1, let's personalize your coverage",
            subTitle: "We have three more questions to help us make a\n recommendation just for you.",
            nextButton: "Let's go",
        },
        signUp: {
            firstName: "First Name",
            lastName: "Last Name",
            dateOfBirth: "Date of Birth",
            plan: "Plan",
            signUp: "Sign Up"
        },
        login: {
            login: "Login",
        },
        limitationsAndExclusions: {
            title: "Limitations and exclusions",
            aCompleteDescr: "A complete description of benefits, limitations, and exclusions will be provided in your Certificate of Insurance and applicable Riders. All coverage is subject to the terms and conditions of the master group policy. We’ve provided a summary below.",
            limitations: "Benefit details",
            brellaWillPay: `${LABEL} will pay a benefit if you or your insured dependent if applicable are diagnosed with a covered condition. You may receive a benefit more than once as noted below`,
            moderateCond: "Moderate condition benefit:",
            severeCond: "Severe condition benefit:",
            catastrophicCond: "Catastrophic condition benefit:",
            aModerateCond: "A Moderate Condition Benefit is only payable once per insured person in a $1-day period. If the same insured person is diagnosed with another Moderate Condition within a $1-day period, it isn’t covered. There is no limit to the number of times an insured person may receive a Moderate Condition Benefit.",
            aSevereCond: "A Severe Condition Benefit is only payable once per insured person in a $1-day period. If the same insured person is diagnosed with another Severe Condition within a $1-day period, it isn’t covered. There is no limit to the number of times an insured person may receive a Severe Condition Benefit.",
            aCatastrophicCond: "A Catastrophic Condition Benefit is only payable once per insured person in a $1-day period. If the same insured person is diagnosed with another Catastrophic Condition within a $1-day period, it isn’t covered. An insured person may only receive this benefit up to $2 times for the same or related condition during the insured person’s lifetime.",
            exclusions: "Exclusions",
            benefitsWillNot: "Benefits will not be payable for an injury or sickness that is contributed to, caused by, or resulting from the following:",
            li1: "commission of or attempt to commit a felony, or voluntary participation in a riot or insurrection;",
            li2: "incarceration or imprisonment following conviction for a crime;",
            li3: "seeking non-emergency treatment during travel or activity outside the United States, Mexico, or Canada;",
            li4: "active duty service or training in the military (naval force, air force or National Guard/Reserves or equivalent) for service/training extending beyond 180 days of any state, country or international organization, unless specifically allowed by a provision of this Certificate;",
            li5: "involvement in any declared or undeclared war or act of war (not including acts of terrorism), while serving in the military or an auxiliary unit attached to the military, or working in an area of war whether voluntarily or as required by an employer;",
            li6: "a chronic condition",
            li7: "a mental illness",
            exceptAsSpecifically: ", except as specifically provided by a rider;",
            li8: "a maternity condition;",
            li9: "a condition considered “not covered” as listed in the conditions list provided with your certificate of insurance.",
            reductionsOfCoverage: "Age Reduction",
            benefitsMayBeReduced: "The benefit amounts in each benefit category will be reduced by $2% once you attain age $1. The benefit amounts for dependent coverage will also be reduced by $2% once the dependent attains age $1.",

            waitingPeriods: "Waiting Periods",
            ifYouEnroll: "If you enroll or add a family member outside of an open enrollment period (annual, newhire, or other qualifying life event), your benefits or any increases are subject to a $1-day waiting period from the effective date of change.",
            renew: "Renewability, Cancellation, & Termination",
            portabilityConditionalText: "You may be eligible to continue, or “port”, your coverage if you leave your employer or if your employer discontinues the group policy.",
            ourPlansAre: "This coverage is offered under a group policy, and is annually renewable. Your continued coverage depends on whether your employer renews the group policy. If your employer does not renew the policy, coverage ends for all employees. If your employer renews the policy, you will have the option to continue your coverage. Premiums may change on the policy anniversary and your employer’s contribution to policy premiums may also change. You may choose to terminate coverage before the next policy anniversary by letting your employer know your choice in writing.",

            copyright: `©${currentYear()} ${INSURANCE_NAME} All Rights Reserved.`,
            brellaIsUnderwritten: `${LABEL} is underwritten by Greenhouse Life Insurance Company (NAIC 80055). Form No. PFSB11-TX`,
            terms: "Terms",
            privacy: "Privacy",
            backToCond: "Back to Conditions list",
        },
        tips: {
            niceToMeetYou: `Does ${LABEL} replace health insurance?`,
            conditionDetailsPage: "Learn more about coverage",
            claims: "Learn more about claims",
            title: "Tips",
            brellaVsHealthIns: `${LABEL}`,
            doesBrellaReplace: `Does AMI replace health insurance?`,
            brellaReplaceDescr: `No, AMI is supplemental insurance that’s designed to complement your primary health insurance. It pays you cash benefits for covered conditions to help with out-of-pocket costs (such as your deductible and copays) and can be used towards things that your health plan doesn’t cover like groceries and transportation.`,
            allAboutCoverage: "All about coverage",
            whatsCovered: "What's covered?",
            wcDecr: "The full list of covered conditions is provided with your Certificate of Insurance and any applicable Riders.",
            wcDescr2: "Please use the conditions list lookup link from the “Whats covered” page",
            wcDescr3: "to look up a specific condition.",
            amICovered: "Am I covered 24 hours a day?",
            amICoveredDescr: `Yes. Since AMI pays benefits based on the diagnosis of a covered condition, it doesn’t matter what time of day you become sick or injured.`,
            doesYourPlan: `Does AMI work with my HSA?`,
            doesYourPlanDescr: `Yes,  AMI is designed to complement your primary health plan, which includes HSA compatible health plans. However, unlike HSA funds that have to be spent on medical expenses, AMI benefits for covered conditions can be used towards anything you need on the road to recovery from medical bills to groceries.`,
            willBrellaCover: `Will AMI cover my medical bills?`,
            willBrellaCoverDescr: `AMI pays a set benefit amount based on the benefit category of your covered condition. Benefits are not based on the cost of treatment or any services you receive. While benefit amounts increase within each benefit category to account for severity, your benefit amount is not directly tied to your medical expenses.`,
            doIHaveToGo: "Do I have to go to an in network doctor to receive benefits?",
            doIHaveToGoDescr: `No, AMI pays benefits for covered conditions based on your diagnosis. It doesn’t matter where you get care as long as you’re diagnosed by a medical professional in the United States, Mexico or Canada.`,
            allAboutClaims: "All about claims",
            whatCountsAs: "What counts as \"proof\" of loss?",
            whatCountsAsDescr: "We realize that different medical events generate different documentation for the patient. We accept a variety of documents to substantiate a claim—you can usually find 2 or 3 items on our list of eligible documents that you have readily available. Some examples of the kinds of documents that may be submitted are hospital discharge paperwork, prescription information, a physician visit summary, or a doctor’s bill.",
            howOftenCanI: "How often can I submit a claim?",
            howLongDoI: "How long do I have to submit a claim after I’m diagnosed?",
            howLongDoIDescr: "You must provide us notice of your claim within 30 days after the diagnosis occurs. If you can’t provide notice within this timeframe, you must send it as soon as reasonably possible. Notice received after 30 days will not invalidate or reduce your claim.",
        },
        getHelp: {
            title: `Message ${LABEL}`,
            descr: `Have a question? Send us a note and we'll get back to you shortly—`,
            fullName: "Full name",
            subject: "Subject",
            contactPreference: "Contact preference",
            email: "Email address",
            phone: "Phone number",
            message: "Message",
            submit: "Submit",
            cancel: "Cancel",
            tz: "ET",
            or: "OR",
            spanishHelp: `Need help in Spanish? Call ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} to receive help.`,
            sendUsAmessage: `Send us a message`,
            emailUs: "Email",
            doNotIncludeSensitiveInformation: "Do not include sensitive information",
        },
        declineCoverageModal: {
            title: "Decline coverage",
            subTitle: "We're sorry to see you go. Please let us know why—",
            reasons: "Reasons",
            explanation: "Explanation ",
            optional: "(Optional)",
            cancel: "Cancel",
            submit: "Submit",
            tellUsMore: "Tell us more...",
            err: "Unable to submit your message",
            itsToo: "It's too expensive",
            iDontThink: "I don't think I need it",
            iDontUnderstand: "I don't understand it",
        },
        formSelectState: {
            notInState: `${LABEL} is not offered in this state`,
            state: "State",
        },
        email: "Email",
        password: "Password",
        step: "STEP",
        back: "Back",
        next: "Next step",
        invalidLink: "Invalid link",
        noOENoElections: {
            note: "Please note...",
            ended: `Open enrollment for $1 has ended. You may be able to enroll in ${LABEL} outside of open enrollment. To learn more,`,
            contact: "contact our team →"
        },
        policyReplacementQuestion : {
            message: "I understand that a person covered by any Title XIX program (Medicaid or any similar name) is not eligible for this coverage.",
            question: "Is the policy being applied for intended to replace any other insurance?",
        },
        or: "or",
        groupClassNotFound: `We're having an issue verifying your eligibility details. Please contact ${LABEL} at ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} or ${STRATEGIC_PARTNER.CONTACT_SUPPORT.EMAIL}.`
    },
    [languages.SPANISH]: {
        expiredToken: {
            title: "Link expirado",
            descr: `Ingresa tu dirección de correo electrónico y le enviaremos un nuevo link para acceder a ${LABEL}:`,
            email: "Correo electrónico",
            sendLink: "Enviar link",
            error: "Error inesperado",
        },
        requestToken: {
            title: "Tu pedido ha sido enviado",
            descr: `Verifica tu correo electrónico por el nuevo link. Si tenemos una cuenta asociada con ese correo electrónico recibirás el nuevo link para acceder a ${LABEL}.`,
            didntReceive: "¿No recibió el correo electrónico?",
            either: "Puede ",
            reSend: "volver a enviar el correo",
            to: " o ",
            tryDiff: "intentar con una dirección de correo electrónico diferente",
            stillNeed: `¿Todavía necesita ayuda? Llame a nuestro equipo ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} o `,
        },
        appHeader: {
            join: `Únete a ${LABEL}`,
            getHelp: "Obtén ayuda",
            tips: "Consejos",
            callUsAt: "Llámanos al",
            monFri: "Lun-vie",
            sat: "Sab",
            forEe: "For Employees",
            forEr: "For Employers",
            forBr: "For Brokers",
            aboutUs: "About Us",
        },
        benAdminWelcome: {
            title: `Hola, te damos la bienvenida a ${LABEL}!`,
            employerPasscode: "Ingresa la clave de acceso de tu empleador",
            associationPasscode: "Ingresa la clave de acceso de tu asociación",
            subTitle: "$1 está ofreciendo un nuevo producto de seguro \npara complementar tu seguro de salud existente.",
            description: `Aún con un genial seguro, los problemas de salud pueden ser caros. ${LABEL} provee soporte financiero rápido cuando estás enfermo o herido.`,
            nextButton: "Siguiente paso",
            invalidInformation: "Información inválida",
            stillHavingTr: "¿Todavía tienes problemas?",
            checkWithYour: `Consulta con tu empleador, o contáctanos al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} `,
            checkWithYourAssociation: `Consulta con tu asociación o contáctanos al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} `,
            or: "o",
            thatPasscode: "Esa clave de acceso no funcionó. Por favor, inténtalo de nuevo.",
        },
        individualWelcome: {
            title: `Te damos la bienvenida a ${LABEL}!`,
            subTitle: `El seguro complementario de ${LABEL} ayuda a cubrir los gastos médicos de bolsillo.`,
            description: `${LABEL} ofrece múltiples planes que se adaptan a tus necesidades. Analicemos tus necesidades de cobertura y veamos cuánto te costará. Una vez listo, confirma tus elecciones y afíliate en cuestión de minutos.`,
            passcode: "Ingresa la clave de acceso",
            nextButton: "Siguiente paso",
            stillHavingTr: "¿Todavía tienes problemas?",
            checkWithYour: `Contáctanos al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            or: "o",
            thatPasscode: "Esa clave de acceso no funcionó. Por favor, inténtalo de nuevo.",
        },
        welcome: {
            title: `Hola $1,<br />te damos la bienvenida a ${LABEL}!`,
            subTitle: `$1 te ofrece un seguro médico complementario de ${LABEL} para ayudarte con los gastos de bolsillo.`,
            description: `${LABEL} ofrece múltiples planes que se adaptan a tus necesidades. Analicemos tus necesidades de cobertura y veamos cuánto te costará. Una vez listo, confirma tus elecciones y afíliate en cuestión de minutos.`,
            nextButton: "Siguiente paso",
            invalidInformation: "Información inválida",
        },
        verifyItsYou: {
            subTitle: "Para asegurarnos que eres tú,\npor favor dinos tu:",
            birthdate: "Fecha de nacimiento",
            ssn: "Número de seguro social",
            nextButton: "Siguiente paso",
            weWereUnable: "No pudimos verificar tu información. Por favor, inténtalo de nuevo.",
            stillHavingTr: "Si todavía tienes problemas, consulta con tu empleador o contáctanos al",
            or: "o",
            accNotFound: "Cuenta no encontrada para este lead convertido",
            enrollmentReceivedDate: "Fecha de recepción de la inscripción",
            classNotFound: "No hemos podido verificar sus datos de elegibilidad",
            pleaseContact: `Por favor póngase en contacto con ${LABEL} al`,
        },
        benVerifyItsYou: {
            subTitle: "Para empezar, por favor, dinos tu:",
            firstName: "Primer nombre",
            birthdate: "Fecha de nacimiento",
            ageImpact: "Tu edad influye en el costo y nos ayuda a personalizar una recomendación para ti.",
            nextButton: "Siguiente paso",
            weWereUnable: "We were unable to verify your information. Please try again.",
            stillHavingTr: "Still having trouble? Check with your employer, or contact us at",
            whereYouLive: "El lugar de residencia influye en la cobertura a la que puede optar.",
            coverageNotAvailableForSelectedState: "Coverage not available for selected state",
            or: "or",
            stateOfResidence: "Estado de residencia",
        },
        steps: {
            step1: `Conoce a AMI`,
            step2: "Personaliza",
            step3: "Selecciona",
            step4: "Revisa",
        },
        learnAboutBrella: {
            title: `Encantados de conocerte, $1.\nSomos ${LABEL}.`,
            subTitle: "Nuestra misión es aliviar el impacto financiero cuando ocurre una adversidad de salud.",
            description: `${LABEL} complementa tu seguro médico principal y te paga un desembolso en efectivo si te diagnostican alguna de las más de 13,000 enfermedades cubiertas. Usa tu dinero para cualquier cosa que necesites en tu camino a la recuperación.`,
        },
        tellUsAboutYou: {
            title: "Cuéntanos un poco más sobre ti",
            description: "Saber más sobre tu familia nos ayudará a recomendarte un plan.",
            maritalStatus: "¿Estás casado(a)?",
            im: "Soy",
            selectOne: "seleccione uno",
            dependents: "¿Tienes hijos?",
            ihave: "Tengo",
            no: "No",
            yes: "Sí",
            noKids: "No tengo hijos",
            aKid: "Sí, mi hijo(a) menor tiene menos de 18 años.",
            aKidGrown: "Sí, mi hijo(a) menor tiene más de 18 años.",
            includesDpAndCu: "Incluye parejas y uniones civiles",
            includesOnlyDp: "Incluye parejas",
            includesOnlyCu: "Incluye uniones civiles",
        },
        thanksWantToHearMore: {
            title: "Gracias, $1",
            subTitle: `Nos encantaría contarte un poco más sobre la forma en que ${LABEL} te ayuda.\nLuego, nos pondremos en la tarea de recomendarte un plan.`,
        },
        whyBrella: {
            title: "Sabemos que estos costos pueden ser alarmantes",
            p1: "Incluso con un buen seguro médico, la mayoría de las personas tienen deducibles y copagos que pueden generar gastos de bolsillo.",
            p2: `${LABEL} ayuda al pagarte una suma global si te diagnostican una enfermedad cubierta. Así es menos probable que los problemas de salud acaben con tus ahorros o se acumulen en tu tarjeta de crédito.`,
            source: "Fuente: Healthcare Bluebook (feb. 2021). Los gastos de bolsillo pueden ser mayores o menores, dependiendo de la cobertura de tu seguro médico.",
            cut: "Una cortadita",
            dehydration: "Deshidratación",
            torn: "Desgarro del LCA",
            appendicitis: "Apendicitis",
            heart: "Ataque cardíaco"
        },
        frustrations: {
            title: `${LABEL} puede ayudarte a aliviar la carga financiera de los gastos médicos inesperados.`,
            subTitle: "Nuestro seguro médico complementario paga un desembolso en efectivo que te ayuda a aliviar la carga de gastos médicos inesperados si te diagnostican una enfermedad cubierta.",
            t1: "Cubrimos más de 13,000 enfermedades",
            t2: "Presentar reclamos es sencillo y fácil",
            t3: "Nuestro equipo te espera",
            d1: `${LABEL} paga desembolsos en efectivo según tu diagnóstico cubierto.`,
            d2: "Presenta un reclamo en minutos con la información que puedas tener a la mano. Una vez aprobado el mismo, recibirás el pago en horas, no en semanas.",
            d3: "Habla con la misma persona cada vez que tengas una pregunta. Siempre estamos aquí para ayudarte.",
        },
        diagnoses: {
            title: `Así funciona ${LABEL}`,
            subTitle: "Cubrimos un amplio espectro de enfermedades, desde contusiones hasta cáncer. Cada enfermedad se asigna a una categoría que determina el monto de su beneficio.",
            payouts: "Las enfermedades de las tres categorías de beneficios están cubiertas—",
            upTo: "Hasta",
            forOverXConditions: "para $1+ condiciones",
            examples: "Ejemplos",
            or: "O",
            coverYourself: "para tu propia cobertura",

            descrM: "Son enfermedades cuyo diagnóstico requiere una visita rápida a la sala de emergencias o atención urgente.",
            exampleM: "Brazo roto, neumonía, contusiones, pequeños cortes y mucho más...",

            descrS: "Condiciones serias que frecuentemente requieren cirugía o tratamiento intensivo",
            exampleS: "Apendicitis, cánceres begningos, rotura de meníscos y mucho más...",

            descrC: "Condiciones peligrosas o mortales que requieren intervención médica urgente",
            exampleC: "Ataque cardiaco, ACB, cánceres malignos y algunas otras cosas...",

            descrNC: "Conditions that can't\nbe treated through\nhome care or a visit\nto your PCP",
            exampleNC: "Gripe\notras cosas que\nno le interesan",

            planStartsAt: "Las primas comienzan a partir de $",
            perPaycheck: "pago de nómina ",
            perPayPeriod: "por periodo de pago ",
            perMonth: "mensual ",

            forFamilyCoverage: "para la cobertura familiar",
            forSpouseCoverage: "para cobertura tuya y de tu cónyuge",
            forChildrenCoverage: "para cobertura tuya y tus hijos",

            footerDescr: "En caso de que le de curiosidad sobre las pocas condiciones que no cubrimos, son condiciones comunes\nque nos pasan a todos de manera rutinaria (ej. dolores de cabeza, resfriado común, etc)- pueden ser duras\nsobre nosotros pero es improbable que nos hagan saltar la banca",
            moderate: "Moderate (Moderada)",
            severe: "Severe (Grave)",
            catastrophic: "Catastrophic (Catastrófica)",

            choose: "Podrás escoger los montos de tus beneficios en unos pocos pasos.",

            imgName:"SPECTRUM_SPANISH",
            moderateConditions: "6,000+ enfermedades",
            severeConditions: "5,600+ enfermedades",
            catastrophicConditions: "1,500+ enfermedades",
        },
        conditionDetails: {
            title: "¿Qué cubrimos? ",
            subTitle: "A continuación, más información sobre la cobertura. No te preocupes, no excluimos las enfermedades preexistentes. La cobertura está garantizado sin ningún interrogatorio médico.",
            fullListLink: "Ver todas las enfermedades",
            fullListDisclaimer: "Incluye enfermedades cubiertas y no cubiertas",
            covers: `${LABEL} cubre las enfermedades en las siguientes tres categorías:`,

            moderateTitle: "Enfermedades moderadas",
            moderateWhatIsTitle: "¿Qué es una enfermedad moderada?",
            moderateWhatIsText: "Lesiones o enfermedades que probablemente implican una visita a urgencias o a la sala de emergencias.",
            moderateCoversTitle: "Cubre más de 6,000+ enfermedades,",
            moderateCoversText: "algunos ejemplos son las fracturas simples, laceraciones, deshidratación y cálculos renales.",
            moderatePayoutContent: "Cada asegurado recibe un monto del beneficio Moderado una vez cada $1 días. No hay límite en el número de desembolsos Moderados que puedes recibir.",

            severeTitle: "Enfermedades graves",
            severeWhatIsTitle: "¿Qué es una enfermedad grave?",
            severeWhatIsText: "Enfermedades serias que requieren atención y tratamiento médico más intensivo.",
            severeCoversTitle: "Cubre más de 5,600+ enfermedades,",
            severeCoversText: "Algunos ejemplos son la apendicitis, fracturas compuestas, embolia pulmonar y desgarro del LCA.",
            severePayoutContent: "Cada asegurado recibe un monto del beneficio Grave una vez cada $1 días. No hay límite en el número de desembolsos Graves que puedes recibir.",

            catastrophicTitle: "Enfermedades catastróficas",
            catastrophicWhatIsTitle: "¿Qué es una enfermedad catastrófica?",
            catastrophicWhatIsText: "Enfermedades peligrosas o potencialmente mortales que requieren una intervención médica inmediata.",
            catastrophicCoversTitle: "Cubre más de 1,500+ enfermedades,",
            catastrophicCoversText: "algunos ejemplos son el cáncer de pulmón, ataque cardíaco, derrame cerebral e insuficiencia orgánica aguda.",
            catastrophicPayoutContent: "Cada asegurado recibe un monto del beneficio Catastrófico una vez cada $1 días. Una persona asegurada puede reclamar un beneficio catastrófico un máximo de $2 veces por diagnósticos de la misma categoría de enfermedades.",

            learnMore: "Más información.",
            hide: "Esconder",
            see: "Mira",
            categoryDetails: "los detalles de esta categoría",

            choiceIncrements: "Elige una suma del beneficio desde ",
            selectPayout: "Elige una suma del beneficio por valor de ",

            whatsNotCovered: "¿Qué excluye la cobertura?",
            uncovered: `${LABEL} no lo cubre todo, y solo está diseñado para cubrir enfermedades moderadas, graves y catastróficas. Consulta todas las enfermedades cubiertas y revisa nuestras Limitaciones y Exclusiones para obtener más información.`,
            or: "o"
        },
        yourBrellaPlanAlsoIncludes: {
            title: "Beneficios Adicionales",
            yourBrellaPlan: "Tu cobertura también incluye estas enfermedades—",
            payoutListed: " y la suma del beneficio está enumerado.",
            chronic: "Enfermedades Crónicas",
            chronicDescription: "crónicas",
            mental: "Enfermedades Mentales",
            maintenanceDescription: "de mantenimiento",
            maintenance: "Mantenimiento",
            maternity: "Maternidad",
            maternityDescription: "maternidad",
            chronicDetails1: "Recibe cobertura para más de ",
            chronicDetails2: " enfermedades $riderLabel adicionales como asma, diabetes, epilepsia y muchas más.",
            chronicDetailsAmount: "4,100",
            mentalDetails1: "Cobertura adicional para ",
            mentalDetails2: " trastornos de salud $riderLabel como ansiedad, trastorno bipolar y depresión.",
            mentalDetailsAmount: "600",
            maternityDetails1: "La cobertura se amplía para incluir una categoría de beneficios adicional para las casi ",
            maternityDetailsAmount: "2,300",
            maternityDetails2: " enfermedades cubiertas relacionadas a la $riderLabel.",
            maternityDetails3: " Un beneficio puede ser pagadero si una persona asegurada queda embarazada o se le diagnostica una enfermedad relacionada a la $riderLabel mientras está cubierta por la póliza.",
            maternityDetails4: " Las pólizas emitidas en Carolina del Norte deben incluir beneficios y cobertura de $riderLabel.",
            nextButton: "Siguiente",
            seeFullList: "Ver la lista completa de enfermedades",
        },
        whatsCovered: {
            title: "Lista de enfermedades",
            subTitle: "Busca entre $1 condiciones",
            mostCommon: "Más común",
            searchingFor: "Buscando por ",
            allOther: "All other",
            bodyPart: "Partes del cuerpo",
            allConditions: "All conditions",
            mostCommonConditions: "Condiciones más comunes",
            nextButton: "Continuar",
            description: "Descripción",
            code: "Código",
            searchFullCond: "Busca en la lista completa de condiciones",
            viewLimit: "Ver Limitaciones y Exclusiones",
            errLoading: "No fue posible cargar la información de las condiciones.",
            search: "Busca",
            totalResults: " resultados para ",
            brellaPays: `${LABEL} paga tus beneficios cuando se te diagnostica alguna de las más de 13,000 enfermedades y lesiones cubiertas por tu póliza. Para obtener más información sobre lo que está y lo que no está cubierto, busca por el código CIE-10, la enfermedad, o bien consulta la lista para ver el diagnóstico. Allí verás las enfermedades enumeradas con la categoría del beneficio asignada en tu contrato. Hemos utilizado términos comunes para referirnos a las lesiones y enfermedades, pero los médicos suelen usar diferentes nombres científicos y asignar diversos códigos de diagnóstico CIE-10 para identificar y distinguir las enfermedades. Para evitar confusiones, cuando presentes un reclamo evaluaremos si tienes o no una enfermedad cubierta en función del código CIE-10 que el médico utilice para describir tu diagnóstico. Si tienes alguna pregunta sobre la cobertura de enfermedades específicas, contáctanos llamando al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} o enviando un correo electrónico a `,
            cantFind: "¿No encontrás lo que necesitás?",
            callBrella: `Llama a un concerje de ${LABEL} al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER}`,
            or: " or ",
            emailOurTeam: "envía un correo electrónico al equipo.",
            includedInPlan: "Incluido",
            notIncludedInPlan: "No incluido",
            viewMore: "Ver más"
        },
        claims: {
            title: "Cómo usar tus beneficios",
            subTitle: "Los reclamos pueden ser una pesadilla",
            description: `Despídete de los formularios impresos y largos periodos de procesamiento. ${LABEL} facilita la presentación de un reclamo para que te paguen en horas, no en semanas.`,
            receiveMedicalAttention: "Recibe atención\nmédica",
            tellUsWhat: "Cuéntanos qué\npasó",
            uploadPhotos: "Carga tus fotos\no documentos",
            analyzeClaim: "Analizaremos tu\nreclamo",
            setPaymentPreference: "Escoge cómo prefieres\nrecibir tu desembolso",
            ifYourClaimIsApproved: "Si tu reclamo es\naprobado, lo pagaremos en\nmenos de 72 horas.",
            otherwiseOurFriendly: "De lo contrario, nuestro equipo se pondrá en contacto contigo para informarte sobre los siguientes pasos.",
            ifYourClaim: "If your claim is approved, we’ll pay it in less than 72 hours.",
            otherwiseOur: "De lo contrario, nuestro equipo se pondrá en contacto contigo para informarte sobre los siguientes pasos.",
        },
        enrollmentProblem: {
            enrollmentIssue: "Problema de inscripción",
            unableToRetrieve: "No podemos obtener la información de tu plan para la\ninscripción. Ponte en contacto con nuestro equipo\nsi tienes alguna duda."
        },
        notEligible: {
            eligibilityIssue: "Problema de elegibilidad",
            basedOnThe: `Con base en la información proporcionada, no eres admisible para la cobertura en este momento.\nSi crees que puede haber un error, contacta a tu empleador.`,
            basedOnTheAssociation: `Con base en la información proporcionada, no eres admisible para la cobertura en este momento.\nSi crees que puede haber un error, contacta a tu asociación.`,
        },
        howCanBrellaHelp: {
            title: `Descubre la forma en que ${LABEL} ayudó a otros`,
            description: `Así trabaja ${LABEL} para las familias ante los problemas de salud de todas las formas y tamaños. Atención: estas no son situaciones reales; son ejemplos realistas que te ayudan a entender mejor como ${LABEL} ayuda.`,
            nextButton: "Encuentra mi plan",
            ray: "Ray, 56 años",
            rayDescr: `Ray es divorciado y padre de dos hijos adultos. Además de ${LABEL}, tiene un seguro médico financiado por su empleador`,
            amber: "Amber, 31 años",
            amberDescr: `Amber es soltera y madre de una niña. Se protege a sí misma y a su hija con un seguro médico y con ${LABEL}.`,
            serena: "Serena, 49 años",
            serenaDescr: `Serena está casada y tiene tres hijos. Además de ${LABEL}, tiene un seguro médico financiado por su empleador.`,
            marcus: "Marcus, 38 años",
            marcusDescr: `Marcus está casado, tiene 4 hijos y toda su familia está protegida por su seguro médico, además de ${LABEL}.`,
        },
        howYourPlanPays: {
            title: `Cómo paga ${LABEL}`,
            description: `${LABEL} es independiente de tu seguro médico. Si te diagnostican una enfermedad cubierta, avísanos al presentar un reclamo. Si lo aprobamos, te pagaremos el monto del beneficio correspondiente a la categoría de tu enfermedad.`,
            payoutsTitle: "Te ayudaremos a establecer el monto del beneficio en unos pocos pasos.",
            payoutComment: "El monto del beneficio es el mismo para todas las enfermedades cubiertas de una categoría, independientemente de lo que cubra o no tu seguro médico.",
            seeHow: `Así pagó ${LABEL}`,
            readMore: "Lee más",
            howBenefited: `Así es como se beneficiaron algunas personas con ${LABEL}.`,
            scenarios: `Ten en cuenta que estos no son casos reales, sino ejemplos realistas que explican cómo ayuda ${LABEL}.`,
            readStories: "Lee las historias de nuestros afiliados",
            moderate: "Moderado",
            severe: "Grave",
            catastrophic: "Catastrófico",
            conditions: "condiciones",
            choiceIncrements: "Elije una suma del beneficio que oscile entre los ",
            selectPayout: "Elije una suma del beneficio por valor de "
        },
        healthPlanDetails: {
            title: "Hablemos de gastos médicos",
            subTitle: "Cuéntanos de tu seguro médico...",
            budget: "Tendremos en cuenta tu presupuesto",
            subTitleFinancial: "Cuéntanos un poco sobre tu situación financiera:",
            doesYourHealth: "¿Crees que su deducible es asequible?",
            yes: "Sí",
            no: "No",
            imNot: "No tengo seguro médico",
            whenVisitingDoctor: "Cuando visitas al doctor o recoge una receta médica",
            iPay: "Normalmente pago los copagos",
            copays: "copago",
            theFullCost: "Normalmente pago el costo total hasta que alcanzo mi deducible.",
            iDontWant: "No quiero una recomendación.",
            skip: "Saltar este paso",
            income: {
                disposableIncome: "Ingresos disponibles",
                afterYouPay: "Después de cubrir tus ",
                necesities: "necesidades",
                houseingCar: " (vivienda, auto, préstamos, servicios públicos, comida, entre otros),\n¿cuánto dinero te queda ",
                eachMonth: "cada mes",
                forDiscretionary: " para gastos opcionales?",
                disclaimer: "*No te preocupes, no compartiremos esta información con tu empleador.",
                disclaimerAssociation: "*No te preocupes, no compartiremos esta información con tu asociación.",
                selectOne: "seleccione una",
                opt1: "Lo gasto todo en mis necesidades",
                opt2: "Puedo comprar 1 o 2 artículos complementarios cada mes",
                opt3: "Generalmente puedo comprar lo que quiero sin pensarlo demasiado",
                opt4: "Puedo comprar lo que quiera y continuar ahorrando",
            },
        },
        plan: {
            title: "Nuestra recomendación para ti",
            basedOn: "Con base en la información proporcionada, te recomendamos lo siguiente: utiliza los controles deslizantes para seleccionar o ajustar tus desembolsos y a quién cubriría.",
            basedOnAlternate: "Con base en la información proporcionada, te recomendamos",
            basedOnAlternate2: "A continuación, puedes elegir o ajustar tu selección y a quién cubre.",
            basedOnModal: "Descubre por qué te damos esta recomendación",
            enhanced: "Enhanced",
            value: "Value",
            premier: "Premier",
            getTips: "y recibe consejos para personalizar cada categoría→",
            descriptionGreen: "Descubre por qué elegimos estas cantidades",
            titleSkipped: `Seleccione sus beneficios ${LABEL}`,
            descriptionSkipped: "¿Necesitas ayuda? Responde 3 preguntas para recibir una",
            descriptionGreenSkipped: "recomendación personalizada",
            conditions: "Condiciones",
            recommended: "Recomendado",
            iWantToCover: "Quiero cobertura para",
            costPer: "Costo por",
            employerContrib: "Contribución de tu empleador",
            employeeContrib: "Tu costo",
            coverMySelf: "A mí mismo(a)",
            coverSpouse: "Mi cónyuge y yo",
            coverChild: "Mi(s) hijo(s) y yo",
            coverFamily: "Mi familia y yo",
            coverSpouseUnderscore: "Mi __cónyuge__ y yo",
            coverChildUnderscore: "Mi(s) __hijo(s)__ y yo",
            costPerOpt1: "Cheque",
            justAMoment: "Un momento...",
            jamDescr: "Estamos personalizando tu plan",
            okay: "Entendido",
            categoryExp: "Explicación de categorías",
            moderate: "Moderado",
            severe: "Grave",
            catastrophic: "Catastrófico",
            emailMyRecommendation: "Enviar mi recomendación por correo electrónico",
            emailMyRecommendationDescr: "Por favor, ingresa tu dirección de correo electrónico para recibir una copia de tus recomendaciones.",
            emailAddress: "Dirección de correo electrónico",
            cancel: "Cancelar",
            submit: "Enviar",
            emailSubmitted: "Tu correo electrónico ha sido enviado",
            submitEmailErrorMessage: "Se ha producido un error y tu correo electrónico no pudo ser enviado. Inténtalo de nuevo más tarde.",
            goToYourEnrollmentPlat: `Visita el programa de inscripción para enviar tus selecciones de ${LABEL}. Si lo deseas, puedes consultar estas recomendaciones cuando te inscribas.`,
            readyToMakeYourSelec: "¿Listo para inscribirte?",
            printRecomm: "Imprimir las recomendaciones",
            emailRecomm: "Enviar por correo las recomendaciones",
            dontSeeCoverageTier: "¿No ves un nivel de cobertura para dependientes? ",
            dontSeeCoverageTierSelect: "¿No ves un nivel de cobertura? Regresa y ",
            editYourFamily: "edita los detalles de tu familia.",
            goBackAndEdit: "Regresa y edita",
            yourFamilyDetails: "los datos de tu familia.",
            descrM: "Algunos ejemplos son las fracturas simples, laceraciones, deshidratación y cálculos renales.",
            descrS: "Algunos ejemplos son la apendicitis, fracturas compuestas, embolia pulmonar y rotura del LCA.",
            descrC: "Algunos ejemplos son el cáncer de pulmón, ataque cardíaco, derrame cerebral e insuficiencia orgánica aguda.",
            seeWhyWeChoseTitle: "Sinopsis de la recomendación",
            modalP1: `Tus necesidades son únicas. Por eso ofrecemos múltiples planes de indemnización médica de ${SHORT_LABEL} (también conocidos como AMI), para que puedas elegir los montos de los beneficios que sean más adecuados para ti. Evaluamos una serie de factores para hacerte una recomendación. A continuación, te explicamos cómo entra en juego cada factor.`,
            modalP2Title: "Edad",
            modalP2: `Los problemas de salud que enfrentamos cambian, en tipo y gravedad, con el tiempo. A medida que envejecemos, aumenta el riesgo de problemas médicos graves y peligrosos, por lo que generalmente recomendamos un plan con montos de beneficios más altos para las categorías de beneficios Graves y Catastróficos, en función de la edad.`,
            modalP3Title: "Situación familiar",
            modalP3: "La estructura de tu familia nos ayuda a entender quién es apto para la cobertura y a hacer algunas suposiciones sobre los riesgos de salud de tu familia. Por su energía y entusiasmo, los niños tienden a tener lesiones y enfermedades menos graves —pero más frecuentes— por lo que te recomendamos planes con beneficios más altos para la categoría Moderada, si tienes hijos.",
            modalP4Title: "Gastos médicos",
            modalP4: "Tu opinión sobre la asequibilidad del deducible de tu plan de salud primario y tus gastos nos da una idea de tu vulnerabilidad financiera en caso de un problema de salud inesperado. Si te preocupa tu deducible y las responsabilidades de los costos de salud, te recomendamos desembolsos más altos en las tres categorías.",
            modalP5Title: "Tu presupuesto",
            modalP5: "Tu ingreso discrecional nos ayuda a recomendarte un plan con primas adaptadas a tu presupuesto. Si no te sobra mucho a fin de mes, podríamos recomendarte un plan de AMI con una prima más asequible. Para las personas con más ingresos disponibles, podemos recomendar una mayor cobertura en función de otros factores como la edad y la situación familiar.",
            seeWhyWeRecommendedTitle: "Por qué recomendamos este plan",
            recommendedForYou: "- Recomendado para usted -",
            iDontWant: `no deseo participar en el plan de seguro de indemnización médica de AIG`,
            or: "o",
            yourOpenEnrollment: "Tu período inscripción abierta comenzará el $1. Vuelve en esta fecha para completar tu selección. No te preocupes, guardaremos tu progreso para cuando regreses.",
            paycheck: 'nómina de pago',
            perPaycheck: 'Por nómina de pago',
            monthly: 'Mensual',
            month: 'mes',
            employerContribution: "Contribución de tu empleador",
            payouts: "Desembolsos",
            important: "IMPORTANTE",
            basedOnBenefit: "Con base en tu edad, recibirás el $1 % de cualquier beneficio que selecciones aquí.",
            nextSteps: "Siguientes pasos",
            includesDpAndCu: "Incluye parejas y uniones civiles.",
            includesOnlyDp: "Incluye parejas.",
            includesOnlyCu: "Incluye uniones civiles.",
            childrenAreElegible: "Según restricciones por edad.",
            paycheckInfo: "Esta cotización se basa en una frecuencia de $1 deducciones de nómina al año, y puede no coincidir con los retiros de nómina exactos. Las primas contractuales son mensuales.",
            sliderWarningLabel: "Revisa esto, por favor.",
            sliderWarningDescription: "El pago de tu desembolso para {{plan1}} debe ser inferior a {{plan2}}.",
            studentStatus: "Tipo de estudiante",
            studentStatusFullTime: "Tiempo completo",
            studentStatusPartTime: "Tiempo parcial",
            studentStatusNotAStudent: "No es un estudiante",
            thisProductIsOnlyAvailable: "Este producto sólo está disponible para las personas que dispongan de un seguro médico completo en vigor.",
        },
        summary: {
            title: "Revisa tu selección",
            description: "Estos son los detalles de la cobertura que has seleccionado:",
            benefitAmounts: "Montos de los beneficios",
            additionalCoverage: "Cobertura adicional",
            employerContrib: "Contribución de tu empleador",
            yourCost: "Tu costo",
            effectiveDate: "Fecha de entrada en vigencia",
            moderate: "Moderado",
            severe: "Grave",
            catastrophic: "Catastrófico",
            whosCovered: "¿A quién cubre?",
            chronic: "Enfermedades Crónicas",
            mental: "Enfermedades Mentales",
            maintenance: "Mantenimiento",
            or: "o",
            goBack: "regresa y cambia",
            important: "IMPORTANTE",
            basedOn: "con base en tu edad, recibirás el $1 % de cualquier beneficio que selecciones aquí.",
            note: "Atención",
            youveAlready: `ya has hecho tus selecciones de ${LABEL}. Si necesitas hacer algún cambio,  `,
            reachOut: "contacta a nuestro equipo →",
            youllReceive: "¡recibirás una notificación para finalizar la configuración de tu cuenta!",
            mo: "mes",
            paycheck: "nómina de pago",
            coverMySelf: "A mí mismo(a)",
            coverSpouse: "A mi cónyuge y a mí",
            coverChild: "A mi(s) hijo(s) y a mí",
            coverFamily: "A mi familia y a mí",
        },
        thanksDeclined: {
            thankYou: "Gracias, $1",
            youHaveSuccessfully: `Has rechazado la cobertura de ${LABEL}.`,
            toMakeAChange: "Para realizar cambios, ",
            contactOurConcierge: "contacta a nuestro equipo →",
        },
        additionalInfo: {
            yourTier: "Cobertura",
            family: "Mi familia y yo",
            title: "Detalles finales",
            homeAddress: "Tu dirección",
            streetAddress: "Dirección",
            zipCode: "Código postal",
            state: "Estado",
            aptSuiteOther: "Apartamento",
            city: "Ciudad",
            dependents: "Tu familia",
            spouse: "Cónyuge",
            addChildT: "Agregar Hijo",
            addSpouse: "Agregar Esposo/a",
            addDependent: "Añadir un(a) dependiente",
            editDependent: "Editar un(a) dependiente",
            yourDetails: "Tus datos",
            firstName: "Primer nombre",
            middleName: "Segundo nombre",
            middleInitial: "Inicial del segundo nombre",
            lastName: "Apellido",
            suffix: "Sufijo",
            dob: "Fecha de nacimiento",
            ssn: "Número de seguro social",
            daytimePhone: "Número de teléfono (día)",
            phoneType: "Tipo de teléfono",
            role: "Rol",
            relationship: "Relación con el empleado",
            domesticPartner: "Pareja",
            civilUnion: "Pareja",
            address: "Dirección",
            gender: "Género",
            sameAsEmployee: "La misma del empleado",
            details: "Detalles",
            cancel: "Cancelar",
            submit: "Submit",
            save: "Guardar",
            optional: "opcional",
            theGreyedOut: "Los campos que aparecen deshabilitados fueron proporcionados por tu empleador, por favor, contáctalo si es necesario que sean corregidos o actualizados.",
            invalidPhoneNumber: "Teléfono inválido",
            invalidZipCode: "Código postal inválido",
            note: "Atención: ",
            reqSpouseChild: "El nivel de cobertura que seleccionaste requiere que añadas a tu cónyuge y al menos un(a) hijo(a).",
            reqSpouse: "El nivel de cobertura que seleccionaste requiere que agregues a tu cónyuge.",
            pleaseAddAnother: "Por favor agrega otro dependiente o ",
            closeAnd: "regresa y cambia ",
            changeCover: "el nivel de cobertura.",
            reqChild: "El nivel de cobertura que seleccionaste requiere que añadas al menos a un(a) hijo(a).",
            delete: "Eliminar",
            confirmDelete: "Confirmar la eliminación",
            dependent: "El dependiente ",
            willBeRemoved: " será eliminado.",
            theCoverageTierDep: " La cobertura seleccionada no incluye dependientes.",
            editDetails: "Editar detalles ",
            coverageNoChild: "El nivel de cobertura que seleccionaste no abarca hijos(as). Por favor, ",
            coverageNoSpouse: "El nivel de cobertura que seleccionaste no abarca a tu cónyuge. Por favor,  agrega otro dependiente o ",
            goBack: "regresa y cambia",
            andChangeCover: " el nivel de cobertura o actualiza el rol del cónyuge.",
            andChangeCoverCh: " tu nivel de cobertura.",
            inOrderToDetermin: `Para determinar si su hijo o hija reúne los requisitos para la cobertura de ${LABEL}, revise y acepte lo siguiente—`,
            inOrderForChildToQualify: `Para que su hijo(a) califique para cobertura de ${LABEL}, debe aplicar lo siguiente—`,
            cIsUnmarried: "Hijo(a) no está casado y no tiene dependientes propios; y",
            cIsFLResident: "Hijo(a) es residente de Florida o es estudiante (debe ser indicado en los campos de arriba).",
            cIsIncapable: "Hijo(a) incapaz de tener un empleo autosuficiente debido a una discapacidad mental o física; y",
            cIsChiefly: "Hijo(a) depende principalmente de ti para su manutención y apoyo; y",
            cProofIs: `Prueba de la discapacidad  requerida por ${LABEL} en el momento de hacer un reclamo.`,
            iAgree: 'Confirmo que mi hijo cumple con los criterios anteriormente mencionados.',
            iAckTheCriteriaAppliesToMyChild: 'Reconozco que las condiciones detalladas arriba aplican a mi hijo',
            female: "Femenino",
            male: "Masculino",
            mobile: "Teléfono celular",
            home: "Teléfono fijo",
            standard: "Estándar",
            child: "Hijo(a)",
            genericErrTitle: "¡Huy!",
            genericErr: "Por favor, corrige los errores anteriores antes de continuar.",
            dependentErr: "Tu nivel de cobertura requiere un dependiente. Añade uno para continuar.",
            basedOnDependentAge: "con base en la edad de tu cónyuge, esta persona recibirá el $1% de los beneficios que selecciones aquí.",
            important: "IMPORTANTE",
            studentStatus: "Estatus de estudiante",
            studentStatusFullTime: "Tiempo completo",
            studentStatusPartTime: "Tiempo parcial",
            studentStatusNotAStudent: "No es estudiante",
            pleaseNoteAboveCriteria: "Ten en cuenta que al menos uno de los criterios anteriores debe cumplirse para hacer clic en guardar.",
            duplicateDependent: "Ya existe un Dependiente con esta información.",
            requestError: "Hubo un error en el pedido",
            firstNameUpdateError: "El Nombre no se puede cambiar.",
            lastNameUpdateError: "El Apellido no se puede cambiar.",
            birthdateUpdateError: "La fecha de nacimiento no se puede cambiar.",
            ssnUpdateError: "El número de seguro social no se puede cambiar.",
            roleUpdateError: "El rol no se puede cambiar",
        },
        review: {
            yes: "Sí",
            no: "No",
            title: "Revisar los detalles",
            nextButton: "Enviar",
            personalDetails: "Datos personales",
            benefitAmounts: "Montos de los beneficios",
            additionalCoverage: "Cobertura adicional",
            dependents: "Dependientes",
            yourCost: "Tu costo",
            effectiveDate: "Fecha efectiva",
            firstName: "Primer nombre",
            lastName: "Apellido",
            email: "Correo electrónico",
            birthdate: "Fecha de nacimiento",
            address: "Dirección",
            city: "Ciudad",
            state: "Estado",
            zipCode: "Código postal",
            moderate: "Moderado",
            severe: "Grave",
            catastrophic: "Catastrófico",
            chronic: "Crónico",
            mental: "Mental",
            maintenance: "Mantenimiento",
            spouse: "Cónyuge",
            domesticPartner: "Pareja",
            civilUnion: "Pareja",
            daughter: "Hija",
            son: "Hijo",
            requestError: "Hubo un error en el pedido",
            enterPassword: "Ingresa la contraseña",
            enterPasswordAgain: "Ingresa la contraseña de nuevo",
            createAccount: "Crear una cuenta",
            createAccountDescr: "Crea una contraseña para completar la creación de tu cuenta. Tu nombre de usuario será tu correo electrónico laboral.",
            looksLikeWeAlready: `Parece que ya tenemos una cuenta de ${LABEL} con este email. Ingrese su password de ${LABEL} para poder definir sus elecciones.`,
            thisMayBe: `Esta puede ser una contraseña usada en el pasado para ${LABEL} empleadores, miembros o Broker.`,
            gender: "Género",
            middleName: "Segundo nombre",
            child: "Hijo(a)",
            employerContribution: "Contribución del empleador",
            ssn: "Número de seguro social",
            edit: "Editar",
            mo: "mes",
            paycheck: "nómina de pago",
            beforeFinal: "Antes de finalizar la inscripción, por favor verifica la información.",
            passwordsMust: "La contraseña debe tener:",
            p1: "10 caracteres.",
            p2: "Al menos una letra mayúscula.",
            p3: "Al menos un número.",
            pErr1: "La contraseña debe tener al menos 10 caracteres",
            pErr2: "La contraseña debe tener al menos un número",
            pErr3: " aLcontraseña debe tener al menos una letra mayúscula",
            pErr4: "Las contraseñas no coinciden",
            pErr5: "Debe aceptar los términos",
            fraudWarning: "ADVERTENCIA DE FRAUDE",
            disclaimer1: "ADVERTENCIA DE FRAUDE: cualquier persona que deliberadamente presente un reclamo falso o fraudulento para el desembolso de una pérdida o beneficio o que deliberadamente presente información falsa en una solicitud de seguro, ha cometido un delito y puede recibir multas e ir a prisión.",
            disclaimer2: "Esta es una póliza que no sustituye a un seguro médico integral y por lo tanto no satisface el requisito de cobertura mínima esencial en virtud de la Ley de Atención Médica Asequible (Affordable Care Act).\n\nAviso: esta póliza proporciona beneficios limitados cuando se te diagnostican ciertas enfermedades. Los montos de los beneficios se basan en si los diagnósticos se clasifican como moderados, graves o catastróficos. No se basan en el costo del tratamiento y no están diseñados para cubrir tus gastos médicos. No todas las enfermedades están cubiertas por el plan.",
            disclaimer3: "Por medio de la presente, elijo inscribirme $1como se indica anteriormente para recibir los beneficios disponibles en el Plan de seguro de beneficios limitados de mi grupo. Al firmar a continuación, acepto $2recibir todos los documentos y la correspondencia por vía electrónica y acepto que puedo acceder a Internet o tengo una dirección de correo electrónico donde puedo recibir correspondencia por correo electrónico. Entiendo que puedo revocar esta autorización o solicitar documentos impresos específicos sin revocar esta autorización al contactar la Compañía (National Union Fire Insurance Company of Pittsburgh, Pa.) o el Administrador (Ansel Services, Inc.) por correo postal, correo electrónico o teléfono.\nCertifico, a mi leal saber y entender, que toda la información proporcionada por mí en este formulario es verdadera y correcta.",
            disclaimerExtraMD: "Este es un complemento del seguro médico y no sustituye a la cobertura médica básica. La falta de cobertura médica básica (u otra cobertura mínima esencial) puede dar lugar a un pago adicional en su declaración de impuestos.",
            disclaimer3Opt1: "y a los miembros de mi familia",
            disclaimer3Opt2: "",
            disclaimer3Opt2b: "autorizando la deducción de la prima necesaria que será debitada para la cobertura seleccionada",
            disclaimer3Opt3: "y entiendo que puedo ",
            note: "Nota:",
            yourBrellaCoverage: `Tu cobertura de ${LABEL} será efectiva cuando se reciba el pago con éxito.`,
            paymentsDetails: "Datos de pago",
            pleaseAddYour: "Por favor, añade tus datos de pago a continuación:",
            byEnteringPayments: `Al introducir tus datos de pago, autorizas a ${SERVICE_NAME} a debitar a la entidad bancaria o financiera que designes el total indicado a continuación.`,
            yourInformationWill: "Tus datos se guardarán para uso en futuras transacciones de tu cuenta. Puedes cancelar esta autorización en cualquier momento contactándote con nosotros. Esta autorización permanecerá en vigor hasta que sea cancelada.",
            youHaveNoEPayment: "¡No tienes configurada la opción de pago electrónico para tu pago inicial!",
            addBankAccount: "Añade tu cuenta bancaria",
            addCreditCard: "Añade tu tarjeta de crédito",
            paymentIssue: "Problema de pago.",
            thereWasAnIssue: "Ha habido un problema de pago con la payment_method que ingresaste. Por favor, comprueba la informacion proporcionada e intentalo de nuevo.",
            or: "o",
            iDontWantToMake: "No quiero hacer un pago electrónico. En su lugar, enviaré un cheque por correo.",
            seeInstructions: "Consulta las instrucciones.",
            mailInYourPayment: "Envía tu pago por correo",
            preferToMailInA: "¿Prefieres enviar un cheque por correo? Descarga el documento PDF y adjúntalo a tu pago.",
            mailTo: "Envíalo por correo a:",
            downloadPDF: "Descargar PDF",
            bankAccount: "Cuenta bancaria",
            creditCard: "Tarjeta de crédito",
            setItAndForgetIt: "Configúralo y olvídalo",
            enableAutoPayToSave: "Activa el Pago Automático para guardar tu método de pago y que tu prima mensual sea debitada automáticamente.",
            weWillNotifyYou: "Te seguiremos avisando antes de cada fecha de pago, y puedes cambiar la configuración del Pago Automático en cualquier momento.",
            cancel: "Cancelar",
            confirm: "Confirmar",
            autoPay: "Pago Automático",
            autoPayIsOn: "Pago Automático activado",
            enableAutoPay: "Pago Automático activado",
            turnOffAutoPay: "¿Desactivar el Pago Automático?",
            areYouSureYouWantToTurnOff: "¿Estás seguro de que quieres desactivar los pagos automáticos? A partir de ahora tendrás que realizar los pagos manualmente.",
            savePaymentMethod: "Guardar esta forma de pago para uso futuro",
            iAuthorizeBrellaServices: `Autorizo a ${SERVICE_NAME} a debitar automáticamente esta fuente de pago para todas las facturas futuras. Puedes cancelar los pagos automáticos o cambiar la configuración en cualquier momento.`,
            checksShouldBe: `Los cheques deben ser pagaderos a ${SERVICE_NAME}`,
            cardNumber: "Número de tarjeta",
            expiration: "Fecha de vencimiento",
            cvc: "Código de seguridad",
            country: "País",
            useDifferentPaymentMethod: "Usa otro método de pago",
            itLooksLikeYouDidntAdd: "Parece que no has añadido un método de pago electrónico. Si continúas sin añadir un método de pago electrónico, esperaremos un pago con cheque por correo. Antes de continuar, vuelve a considerar la posibilidad de utilizar una opción de pago electrónico. Si el pago no se recibe a tiempo, podría resultar en un retraso en tu cobertura.",
            paymentProcessing: "Pago en proceso...",
            pleaseDoNotRefresh: "Por favor, no actualices ni cierres tu sesión, ",
            thisProcessMakeTake: "este proceso puede tardar hasta un minuto.",
            iAcceptThe: "Acepto los",
            terms: "términos y condiciones",
            and: "y",
            privacy: "la póliza de privacidad",
            ccEndingWith: "Últimos cuatro números:",
            goBack: "Regresar",
            confirmAndSubmit: "Confirmar y enviar",
            pendingVerifiction: "Pendiente de verificación",
            removePendingverificationModalHeader: "¿Quieres utilizar otro método de pago?",
            removePendingverificationModalContent: "Si cambias de métodoe de pago perderás el status de verificación de la cuenta bancaria. Volver a verificarla puede llevar hasta dos días hábiles",
            removePendingverificationModalConfirm: "Confirmar",
            addBankAccountError: "Ha habido un error al añadir la cuenta bancaria.",
        },
        success: {
            title: "¡Gracias, $1!",
            description: `Tu información ha sido enviada con éxito. ¡Te damos la bienvenida a ${LABEL}!. `,
            next: `A continuación, busca un correo electrónico de nosotros confirmando tus elecciones y con instrucciones para configurar tu cuenta de ${LABEL}.`,
            allDone: "¡Terminaste!",
            feelFree: "Puedes cerrar la pestaña de tu navegador o",
            logOut: "cerrar la sesión.",
            yourCoverage: "Tu cobertura empieza el"
        },
        categories: {
            moderate: "Moderado",
            severe: "Grave",
            catastrophic: "Catastrófico",
            weDontCover: "No cubrimos"
        },
        brellaHelp: {
            title: `Descubre la forma en que ${LABEL} ayudó a $1`,
            viewMoreStories: "Ver más historias",
            coverage: `La cobertura de ${LABEL} para $1`,
            moderate: "Moderada",
            severe: "Grave",
            catastrophic: "Catastrófica",
            perPaycheck: "Per paycheck",
            priceIllustrative: "This price is for illustrative purposes only",

            story: "La historia de $1",

            title2: `Donde entra ${LABEL}`,
            personPlan: `Plan de ${LABEL} para $1`,
            personPaid: "$1 pagó",
            whoSCovered: "Quien está cubierto",
            costPaycheck: "Costo/cheque",
            brellaBenefitAmounts: `Cantidad de beneficios de ${LABEL}`,
            nextButton: "Entendido",
            unexpectedExpenses: "en expensas inesperadas",
            brellaCoverage: `${LABEL} cubrió TODOS los costos de $1 y más`,
            paymentsTitle: "Los reclamos de $1",
            paymentsTitleSingluar: "El reclamo de $1",
            brellaPayout: `${LABEL}'s payout`,
            brellaCash: `Total de los beneficios de ${LABEL} que $1 recibe`,
            brellaCashSingular: `Total del beneficio de ${LABEL} que $1 recibe`,
            subtitle: "El escenario que se resume a continuación se ofrece solo a modo de ejemplo. La cobertura depende de los hechos reales de cada caso y de los términos, condiciones y exclusiones de la póliza.",
            s1: {
                ray: "Ray, 56 años",
                description: `Ray es un padre divorciado con dos hijos adultos. Su empleador financia su seguro médico y el plan de ${LABEL}. El deducible de su seguro médico es de $1,500.`,
                injury1Title: "Diagnóstico de COVID",
                injury1Desc: "Al bajarle la fiebre, Ray resultó positivo para COVID-19. Sus síntomas fueron leves al principio, pero luego padeció serias complicaciones",
                injury2Title: "Complicaciones graves",
                injury3Title: `${LABEL} te ayuda`,
                injury2Desc: "Durante varias semanas Ray padeció neumonía, insuficiencia respiratoria aguda y sepsis. Afortunadamente, su condición mejoró y fue dado de alta del hospital.",
                injury3Desc: "Ray presentó los reclamos y recibió los montos del beneficio por neumonía, insuficiencia respiratoria aguda y sepsis. Usó el efectivo para pagar las facturas médicas hasta alcanzar el límite de sus gastos imprevistos y para contratar a alguien que le ayudara con los víveres y el césped hasta que se recuperó por completo.",
                payout1: "La neumonía recibe una suma del beneficio por categoría Moderada por valor de",
                payout2: "La insuficiencia respiratoria aguda tiene una suma del beneficio por categoría Grave por valor de",
                payout3: "La sepsis tiene una suma del beneficio por categoría Catastrófica por valor de"
            },
            s2: {
                amber: "Amber, 31 años",
                description: `Amber es soltera y madre de una niña. Se protege a sí misma y a su hija con un seguro médico y con ${LABEL}. El deducible de su seguro médico es de $1,000.`,
                injury1Title: "Cirugía de apendicitis",
                injury1Desc: "Cuando Amber se despertó con un dolor de estómago punzante, fue a la sala de emergencia y allí fue operada de apendicitis.",
                injury2Title: "Presentó un reclamo en pocos minutos...",
                injury2Desc: `Presentó un reclamo en ${LABEL} desde su teléfono el día que salió del hospital.`,
                injury3Title: "...y recibió su desembolso en 72 horas",
                injury3Desc: "El reclamo de Amber fue aprobado y pagado en 72 horas. Usó el dinero para pagarle a una niñera y para pedir víveres a domicilio. También pagó la factura que llegó semanas después.",
                payout1: "La apendicitis tiene una suma del beneficio de categoría Grave por valor de"
            },
            s3: {
                serena: "Serena, 49 años",
                description: `Serena está casada y tiene tres hijos. Su empleador financia su seguro médico y el plan de ${LABEL}. El deducible de su seguro médico es de $5,000.`,
                injury1Title: "Diagnóstico de cáncer de seno",
                injury1Desc: "A Serena se le diagnosticó cáncer de seno este año. Presentó un reclamo y fue aprobada para recibir un beneficio por categoría Catastrófica de $7,500.",
                injury2Title: "Tratamiento en curso",
                injury2Desc: "Tres meses después, seguía en tratamiento y recibió otro beneficio por categoría catastrófica de $7,500.",
                injury3Title: `${LABEL} te ayuda`,
                injury3Desc: "Serena pagó sus facturas médicas con los desembolsos hasta que alcanzó su límite de gastos imprevistos. El resto del dinero ayudó con el transporte y la ayuda extra en la casa.",
                payout1: "El cáncer de seno recibe una suma del beneficio Catastrófico por valor de",
                payout2: "El cáncer de seno recibe un segundo monto del beneficio Catastrófico por valor de"
            },
            s4: {
                marcus: "Marcus, 38 años",
                description: `Marcus está casado, tiene 4 hijos, y toda su familia está protegida por un seguro médico y por ${LABEL}. El deducible de su seguro médico es de $3,000.`,
                injury1Title: "Una pierna fracturada",
                injury1Desc: "Marcus ha tenido un año difícil. En enero, se cayó de una escalera y se fracturó el fémur.",
                injury2Title: "Gastroenteritis viral",
                injury2Desc: "Semanas después, su hija contrajo una gastroenteritis viral e ingresó a la sala de emergencia por deshidratación.",
                injury3Title: "Su recuperación",
                injury3Desc: `Marcus presentó sus reclamos y recibió los beneficios de la indemnización médica de AIG, que utilizó para pagar facturas médicas hasta alcanzar su deducible. Los beneficios de AIG aliviaron la carga financiera de estos costos inesperados.`,
                payout1: "Una fractura de fémur recibe una suma del beneficio por categoría Grave por valor de",
                payout2: "La deshidratación recibe una suma del beneficio por categoría Moderada por valor de"
            }
        },
        wouldYouMind: {
            iDontWant: "No quiero recomendaciones,",
            skip: "saltar este paso",
            title: "$1, vamos a personalizar tu cobertura",
            subTitle: "Tenemos tres preguntas más que nos ayudarán\na darte una recomendación específica",
            nextButton: "Vamos",
        },
        signUp: {
            firstName: "Primer Nombre",
            lastName: "Apellido",
            dateOfBirth: "Fecha de Nacimiento",
            plan: "Plan",
            signUp: "Regístrese"
        },
        login: {
            login: "Ingresar",
        },
        limitationsAndExclusions: {
            title: "Limitaciones y exclusiones",
            aCompleteDescr: "Podrás encontrar una descripción completa de los beneficios, limitaciones y exclusiones en tu Certificado de seguro y en las cláusulas aplicables. La cobertura está sujeta a los términos y condiciones de la póliza de grupo maestra. A continuación, te presentamos un resumen de esta.",
            limitations: "Detalles de beneficio",
            brellaWillPay: `${LABEL} pagará un beneficio si a ti o a tu dependiente asegurado (si corresponde) se le diagnostica una enfermedad cubierta. Puedes recibir un beneficio más de una vez, como se indica a continuación, en función de un período de separación definido.`,
            moderateCond: "BENEFICIO POR ENFERMEDADES MODERADAS:",
            severeCond: "BENEFICIO POR ENFERMEDADES GRAVES:",
            catastrophicCond: "BENEFICIO POR ENFERMEDADES CATASTRÓFICAS:",
            aModerateCond: "El beneficio por Enfermedad Moderada solo se paga una vez por persona asegurada en un período de $1 días. Si a esta misma persona asegurada se le diagnostica otra Enfermedad Moderada en un período de $1 días, esta enfermedad no estará cubierta. No hay límite en el número de veces que una persona asegurada puede recibir un beneficio por Enfermedades Moderadas.",
            aSevereCond: "El beneficio por Enfermedad Grave solo se paga una vez por persona asegurada en un período de $1 días. Si a esta misma persona asegurada se le diagnostica otra Enfermedad Grave en un período de $1 días, esta enfermedad no estará cubierta. No hay límite en el número de veces que una persona asegurada puede recibir un beneficio por Enfermedades Graves.",
            aCatastrophicCond: "El beneficio por Enfermedad Catastrófica solo se paga una vez por persona asegurada en un período de $1 días. Si a esta misma persona asegurada se le diagnostica otra Enfermedad Catastrófica en un período de $1 días, esta enfermedad no estará cubierta. Una persona asegurada solo puede recibir este beneficio hasta $2 veces por la misma enfermedad o una relacionada, durante su vida.",
            exclusions: "Exclusiones",
            benefitsWillNot: "No se pagarán beneficios por una lesión o enfermedad que  contribuya, sea causada o resulte de lo siguiente:",
            li1: "la acción o intento de cometer un delito grave, o la participación voluntaria en un motín o insurrección que dé lugar a una condena judicial o a la admisión de culpabilidad;",
            li2: "El encarcelamiento posterior a la condena por un delito.",
            li3: "La búsqueda de un tratamiento que no sea de emergencia durante un viaje o actividad fuera de los Estados Unidos, México o Canadá.",
            li4: "El servicio activo o formación en el ejército (Fuerza Naval, Fuerza Aérea, Guardia Nacional, Reservas Nacionales o sus equivalentes) que exceda los 180 días en cualquier estado, país u organización internacional, a menos que haya autorización específica en una disposición de este Certificado.",
            li5: "La participación en cualquier guerra o acto de guerra, declarada o no (excluyendo actos de terrorismo), durante el servicio en las Fuerzas Armadas —o en una unidad auxiliar adscrita a ellas— o el desempeño en una zona de guerra, ya sea voluntariamente o por exigencia de un empleador.",
            li6: "Una enfermedad crónica",
            li7: "Una enfermedad mental",
            exceptAsSpecifically: ", excepto si está estipulada específicamente en una cláusula adicional",
            li8: "Maternidad.",
            li9: "Enfermedades sin cobertura, según la lista de enfermedades de tu Certificado de seguro.",
            reductionsOfCoverage: "Reducción por edad",
            benefitsMayBeReduced: "Los montos de los beneficios en cada categoría de beneficios se reducirán en un $2% una vez que cumplas $1 años. Los montos de los beneficios para la cobertura de los dependientes también se reducirán en un $2% una vez que el dependiente cumpla $1 años.",

            waitingPeriods: "Períodos de espera",
            ifYouEnroll: "Si te inscribes o agregas a un familiar fuera de un período de inscripción abierta (anual, de nueva contratación u otro evento calificado), tus beneficios o cualquier ajuste en las cuotas  estarán sujetos a un período de espera de $1 días a partir de la fecha de vigencia del cambio.",
            renew: "Renovación, cancelación y anulación",
            portabilityConditionalText: "Puedes optar por continuar o traspasar tu cobertura si dejas tu empresa o si está interrumpe la póliza de grupo.",
            ourPlansAre: "Esta cobertura se ofrece en el marco de una póliza colectiva y es renovable anualmente. La continuidad de tu cobertura está condicionada por la renovación de la póliza de grupo por parte de tu empleador. Si tu empleador no renueva la póliza, la cobertura terminará para todos los empleados. Si tu empleador renueva la póliza, tendrás la opción de continuar con tu cobertura. Las primas pueden cambiar en el aniversario de la póliza y la cotización de tu empleador a estas también puede cambiar. Puedes optar por suspender la cobertura antes del siguiente aniversario de la póliza comunicando tu decisión por escrito a tu empleador.",

            copyright: `©${currentYear()} ${INSURANCE_NAME} Todos los derechos reservados.`,
            brellaIsUnderwritten: `${LABEL} está financiada por la Compañía de seguros de vida de Greenhouse (Asociación Nacional de Comisionados de Seguros “NAIC” 80055). Formulario No. PFSB11-TX`,
            terms: "Términos",
            privacy: "Privacidad",
            backToCond: "Regresa a la lista de enfermedades",
        },
        tips: {
            niceToMeetYou: `¿${LABEL} reemplaza al seguro médico?`,
            conditionDetailsPage: "Más información sobre la cobertura",
            claims: "Más información sobre los reclamos",
            title: "Consejos",
            brellaVsHealthIns: `${LABEL} y los seguros médicos`,
            doesBrellaReplace: `¿${LABEL} reemplaza al seguro médico?`,
            brellaReplaceDescr: `No, AMI es un seguro suplementario que está diseñado para complementar tu seguro médico primario. AMI te paga desembolsos en efectivo por las enfermedades cubiertas para ayudarte con los gastos imprevistos (como los deducibles y los copagos) y puede ser usado para las cosas que tu plan médico no cubre, como los víveres y el transporte.`,
            allAboutCoverage: "Todo lo relacionado a la cobertura",
            whatsCovered: "¿Qué está cubierto?",
            wcDecr: "Podrás hallar la lista completa de las enfermedades cubiertas en tu Certificado de seguro y en cualquier cláusula aplicable.",
            wcDescr2: "Utiliza el enlace que lleva a la búsqueda de la lista de enfermedades en la página “¿Qué está cubierto?”",
            wcDescr3: " para encontrar una enfermedad específica.",
            amICovered: "¿Cuento con cobertura las 24 horas del día?",
            amICoveredDescr: `Sí. AMI paga los beneficios al momento del diagnóstico, así que no importa la hora del día en que te enfermes o te lesiones.`,
            doesYourPlan: `¿AMI funciona con mi cuenta de ahorro para la salud (HSA)? `,
            doesYourPlanDescr: `Sí, AMI está diseñado para complementar tu plan médico primario, lo cual incluye planes médicos compatibles con las cuentas de ahorro para la salud. Pero, a diferencia de los fondos de las HSA que deben ser usados solo para gastos médicos, los beneficios de AMI para las enfermedades cubiertas se pueden utilizar para cualquier cosa que necesites en el camino hacia tu recuperación, desde facturas médicas hasta víveres.`,
            willBrellaCover: `¿AMI cubre mis facturas médicas?`,
            willBrellaCoverDescr: `AMI paga los beneficios en montos fijos con base en la categoría de beneficios de tu enfermedad cubierta. Estos beneficios no están basados en el costo del tratamiento o del servicio que recibas. Aunque los pagos de cada categoría de beneficios aumentan para reflejar la gravedad, el pago de tu beneficio no está directamente vinculado a tus gastos médicos.`,
            doIHaveToGo: "¿Tengo que ir a un médico que pertenezca a la red para recibir los beneficios?",
            doIHaveToGoDescr: `No, AMI paga los beneficios con base en tu diagnóstico. No importa dónde recibas atención médica, siempre y cuando te diagnostique un profesional médico en los Estados Unidos, México o Canadá.`,
            allAboutClaims: "Todo lo relacionado a reclamos",
            whatCountsAs: "¿Qué cuenta como prueba de pérdida?",
            whatCountsAsDescr: "Sabemos que cada evento médico genera diferente documentación para el paciente, por eso aceptamos una variedad de documentos para fundamentar un reclamo; normalmente dispondrás de 2 o 3 artículos de nuestra lista de documentos aptos. Algunos ejemplos de los documentos que se pueden presentar son: los documentos de alta del hospital, la receta médica, el informe médico o la factura médica.",
            howOftenCanI: "¿Con qué frecuencia puedo presentar un reclamo?",
            howLongDoI: "¿Cuánto tiempo tengo para presentar un reclamo después de que me diagnostiquen?",
            howLongDoIDescr: "Tienes un plazo de 30 días para presentar tu reclamo después de que te diagnostiquen. Si no puedes proporcionar esta notificación dentro de este plazo, debes enviarla tan pronto como sea razonablemente posible. Si la notificación es recibida después de 30 días, esto no invalidará ni reducirá tu reclamo",
        },
        getHelp: {
            title: `Envía un mensaje a ${LABEL}`,
            descr: `¿Tienes preguntas? Envíanos un mensaje y te contactaremos pronto.`,
            fullName: "Nombre completo",
            subject: "Asunto",
            contactPreference: "Preferencias de contacto",
            email: "Correo electrónico",
            phone: "Número de teléfono",
            message: "Mensaje",
            submit: "Enviar",
            cancel: "Cancelar",
            tz: "horario del este",
            or: "O",
            spanishHelp: `¿Necesitas ayuda en español? Llama al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} para recibir ayuda.`,
            sendUsAmessage: "Envíanos un mensaje",
            emailUs: "Correo electrónico",
            doNotIncludeSensitiveInformation: "Evita incluir información sensible",
        },
        declineCoverageModal: {
            title: "Rechazar la cobertura",
            subTitle: "Lamentamos que no quieras inscribirte. Por favor, dinos por qué.",
            reasons: "Razones:",
            explanation: "Explicanos por qué ",
            optional: "(opcional)",
            cancel: "Cancelar",
            submit: "Enviar",
            tellUsMore: "Cuéntanos más...",
            err: "Error al enviar tu mensaje",
            itsToo: "Es demasiado costoso;",
            iDontThink: "No creo necesitarlo;",
            iDontUnderstand: "No lo entiendo;",
        },
        formSelectState: {
            notInState: `${LABEL} no está disponible en este estado.`,
            state: "Estado",
        },
        email: "Correo Electrónico",
        password: "Contraseña",
        step: "PASO",
        back: "Atrás",
        next: "Paso Siguiente",
        invalidLink: "Link inválido",
        noOENoElections: {
            note: "Por favor tenga en cuenta...",
            ended: `Las inscripciones para $1 han terminado. Puede inscribirse en ${LABEL} fuera del período de inscripción. Para conocer más,`,
            contact: "contacta a nuestro equipo →"
        },
        policyReplacementQuestion: {
            message: "Entiendo que una persona cubierta por cualquier programa del Título XIX (Medicaid o cualquier nombre similar) no es elegible para esta cobertura.",
            question: "¿La póliza para la cual se está solicitando está destinada a reemplazar algún otro seguro?",
        },
        or: "o",
        groupClassNotFound: `No hemos podido verificar sus detalles de elegibilidad. Contacte al equipo de soporte llamando al ${STRATEGIC_PARTNER.CONTACT_SUPPORT.NUMBER} o envíe un mensaje a ${STRATEGIC_PARTNER.CONTACT_SUPPORT.EMAIL}.`
    }
};

